/**
 * InfraStorefront — Task #222.
 *
 * Shared component that renders the sending-infrastructure package catalog,
 * purchase history, and provisioned items table.  Used in:
 *   - Tenant workspace  (surface="tenant")  — /admin/email-hub → Sending Domains tab
 *   - Platform workspace (surface="platform") — /platform/infra
 *
 * The `surface` prop drives which API paths to hit and which extra controls to show.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Server, Globe, MailCheck, ShoppingCart, Loader2, CheckCircle2,
  XCircle, Clock, RefreshCw, AlertTriangle, Package, Plus, Pencil,
  Trash2, BarChart3, TrendingUp, Search, Settings2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { COLD_SEND_TIER_GUIDE, PLACEMENT_TEST_DAILY_BUDGET, WARMUP_GRADUATION_GATE } from "@shared/cold-send-caps";
import { planMailboxAddresses, findDuplicateMailboxAddress } from "@shared/cold-mailbox-names";

// ─── Warm-up ramp config ──────────────────────────────────────────────────────
// Single source of truth: adjust these numbers here and the disclosure table
// in the purchase confirmation will reflect the change automatically.

interface WarmupStage {
  label: string;       // e.g. "Days 1–30"
  perMailboxPerDay: number;
}

const WARMUP_RAMP: WarmupStage[] = [
  { label: "Days 1–30",   perMailboxPerDay: 20  },
  { label: "Days 31–60",  perMailboxPerDay: 50  },
  { label: "Days 61–90",  perMailboxPerDay: 100 },
  { label: "Day 91+",     perMailboxPerDay: 0   }, // 0 = full / uncapped
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InfraPackage {
  id: string;
  name: string;
  description: string | null;
  purchaseType: "lease" | "buy";
  domainsCount: number;
  mailboxesCount: number;
  wholesaleCentsPerDomain: number;
  wholesaleCentsPerMailbox: number;
  retailCentsPerDomain: number;
  retailCentsPerMailbox: number;
  domainBillingCycle: "annual" | "monthly";
  mailboxBillingCycle: "annual" | "monthly";
  /** 'per_item' = separate annual-domain + monthly-mailbox Stripe charges (legacy).
   *  'flat_bundle' = one flat monthly Stripe charge for the entire bundle. */
  billingMode: "per_item" | "flat_bundle";
  flatRateMonthlyWholesaleCents: number;
  flatRateMonthlyRetailCents: number;
  isActive: boolean;
  sortOrder: number;
}

export interface InfraPurchase {
  id: string;
  workspaceId: string;
  packageId: string | null;
  providerOrderId: string | null;
  domainsCount: number;
  mailboxesCount: number;
  retailCents: number;
  wholesaleCents: number;
  marginCents: number;
  provisioningStatus: "pending_payment" | "provisioning" | "awaiting_capacity" | "live" | "failed" | "cancelled" | "replacement_requested";
  /** Where a PLATFORM-built carousel lives: sandbox / lease pool / reserved.
   *  Only meaningful for purchases still owned by the platform sentinel. */
  poolDisposition?: "unassigned" | "leasable" | "platform_use";
  provider: string;
  notes: string | null;
  createdAt: string;
  items?: InfraItem[];
  /** Wizard extras stored at checkout — includes fromName, forwardingEmail, etc. */
  provisionSpec?: { fromName?: string; [key: string]: unknown };
}

export interface InfraItem {
  id: string;
  purchaseId: string;
  workspaceId: string;
  itemType: "domain" | "mailbox";
  identifier: string;
  status: "provisioning" | "live" | "failed" | "cancelled" | "replacement_requested";
  providerItemId: string | null;
  emailSendingDomainId: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Derive the current warmup stage from the item's provisioning date.
// Returns day count, current cap, next cap, and fraction through the 90-day ramp.
function getWarmupInfo(createdAt: string): {
  daysSince: number;
  currentCap: number | null; // null = fully ramped (uncapped)
  nextCap: number | null;    // null = already at final stage
  stagePct: number;          // 0–100 progress through the 90-day ramp
  stageLabel: string;
} {
  const ms = Date.now() - new Date(createdAt).getTime();
  const daysSince = Math.max(0, Math.floor(ms / 86_400_000)) + 1; // 1-indexed

  const RAMP_DAYS = 90;
  const stagePct = Math.min(100, Math.round((daysSince / RAMP_DAYS) * 100));

  // Find which WARMUP_RAMP bucket we're in
  const stages = WARMUP_RAMP.filter((s) => s.perMailboxPerDay > 0); // exclude Day 91+
  let stageIndex = stages.length; // default = fully ramped
  for (let i = 0; i < stages.length; i++) {
    // Parse the upper day bound from the label (e.g. "Days 1–30" → 30, "Days 31–60" → 60)
    const match = stages[i].label.match(/\d+[^\d]+(\d+)/);
    const upperDay = match ? parseInt(match[1], 10) : 0;
    if (daysSince <= upperDay) {
      stageIndex = i;
      break;
    }
  }

  const fullyRamped = stageIndex >= stages.length;
  const currentCap = fullyRamped ? null : stages[stageIndex].perMailboxPerDay;
  const nextCap = fullyRamped || stageIndex >= stages.length - 1
    ? null
    : stages[stageIndex + 1].perMailboxPerDay;

  const stageLabel = fullyRamped
    ? "Fully ramped"
    : (WARMUP_RAMP[stageIndex]?.label ?? `Day ${daysSince}`);

  return { daysSince, currentCap, nextCap, stagePct, stageLabel };
}

// Compact inline progress indicator shown on live mailbox items.
function WarmupProgress({ createdAt }: { createdAt: string }) {
  const { daysSince, currentCap, stagePct, stageLabel } = getWarmupInfo(createdAt);
  const isFullyRamped = currentCap === null;

  return (
    <div
      className="flex items-center gap-2 mt-1"
      data-testid="warmup-progress"
    >
      <TrendingUp className="h-3 w-3 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-xs text-muted-foreground">
            {isFullyRamped ? (
              "Warm-up complete — no daily cap"
            ) : (
              <>
                Day {daysSince} of 90 — <strong>{currentCap} emails/mailbox/day</strong>
              </>
            )}
          </span>
          <Badge
            variant={isFullyRamped ? "default" : "secondary"}
            className="text-[10px] px-1.5 py-0 h-4 shrink-0"
            data-testid="badge-warmup-stage"
          >
            {stageLabel}
          </Badge>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              isFullyRamped ? "bg-green-500" : "bg-primary",
            )}
            style={{ width: `${stagePct}%` }}
            data-testid="bar-warmup-progress"
          />
        </div>
      </div>
    </div>
  );
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    live: { label: "Live", variant: "default" },
    provisioning: { label: "Provisioning…", variant: "secondary" },
    awaiting_capacity: { label: "Awaiting Capacity", variant: "secondary" },
    pending_payment: { label: "Pending Payment", variant: "outline" },
    failed: { label: "Failed", variant: "destructive" },
    cancelled: { label: "Cancelled", variant: "outline" },
    replacement_requested: { label: "Replacement Requested", variant: "secondary" },
  };
  const info = map[status] ?? { label: status, variant: "outline" as const };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

function statusIcon(status: string) {
  if (status === "live") return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === "cancelled") return <XCircle className="h-4 w-4 text-muted-foreground" />;
  return <Clock className="h-4 w-4 text-yellow-500" />;
}

// ─── Buy dialog ───────────────────────────────────────────────────────────────

export function BuyDialog({
  pkg,
  mode,
  surface,
  workspaceIdOverride,
  onClose,
}: {
  pkg: InfraPackage;
  mode: "lease" | "buy";
  surface: "tenant" | "platform";
  workspaceIdOverride?: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  // Task #450: lease mode is always "automatic" — keyword/exact tabs are buy-only.
  const [domainMode, setDomainMode] = useState<"automatic" | "keyword" | "exact">(
    mode === "lease" ? "automatic" : "automatic",
  );
  const [domainKeyword, setDomainKeyword] = useState("");
  const [exactNames, setExactNames] = useState<string[]>(() =>
    Array(Math.max(0, pkg.domainsCount)).fill(""),
  );
  // Brand-agnostic lease suggestions (Task #403). Fetched on demand and
  // re-fetched via "Regenerate"; these are the names we'll register at checkout.
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // Task #450 — keyword tab availability check results.
  const [keywordSuggestions, setKeywordSuggestions] = useState<string[]>([]);
  const [keywordChecked, setKeywordChecked] = useState(false);
  // Task #450 — exact tab availability check results.
  const [exactAvailability, setExactAvailability] = useState<Record<string, boolean>>({});
  // Confirmation gate: null = no names confirmed yet; non-null = user explicitly
  // locked in this set via a "Confirm" action. Submit is disabled while null.
  const [confirmedDomainNames, setConfirmedDomainNames] = useState<string[] | null>(null);

  // ── Platform-surface domain selection ──────────────────────────────────────
  // For buy: operator picks via keyword or exact name entry (same UI as tenant).
  // For lease: operator picks from domains already in the Maildoso account.
  const [platformBuyMode, setPlatformBuyMode] = useState<"keyword" | "exact">("keyword");
  const [platformKeyword, setPlatformKeyword] = useState("");
  const [platformKeywordResults, setPlatformKeywordResults] = useState<string[]>([]);
  const [platformKeywordChecked, setPlatformKeywordChecked] = useState(false);
  const [platformExactNames, setPlatformExactNames] = useState<string[]>(() =>
    Array(Math.max(1, pkg.domainsCount)).fill(""),
  );
  const [platformExactAvail, setPlatformExactAvail] = useState<Record<string, boolean>>({});
  // For lease: which platform pool carousel the operator selected (by purchaseId).
  // (platformSelectedDomains removed — pool picker uses platformSelectedCarousel)

  const suggestionsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/admin/infra/domain-suggestions?count=${pkg.domainsCount}`,
      );
      const data = await res.json();
      return (data.suggestions as string[]) ?? [];
    },
    onSuccess: (s) => { setSuggestions(s); setConfirmedDomainNames(null); },
    onError: (err: any) =>
      toast({
        title: "Couldn't load suggestions",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  // Task #450 — keyword tab: check availability based on keyword seed.
  const keywordCheckMutation = useMutation({
    mutationFn: async () => {
      const seed = domainKeyword.trim();
      if (!seed) throw new Error("Please enter a keyword first.");
      const res = await apiRequest(
        "GET",
        `/api/admin/infra/domain-suggestions?count=${pkg.domainsCount}&keyword=${encodeURIComponent(seed)}`,
      );
      const data = await res.json();
      return (data.suggestions as string[]) ?? [];
    },
    onSuccess: (s) => {
      setKeywordSuggestions(s);
      setKeywordChecked(true);
      setConfirmedDomainNames(null);
    },
    onError: (err: any) =>
      toast({
        title: "Couldn't check availability",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  // Task #450 — exact tab: check availability of the typed domain names.
  const exactCheckMutation = useMutation({
    mutationFn: async () => {
      const names = exactNames.map((n) => n.trim().toLowerCase()).filter(Boolean);
      if (names.length === 0) throw new Error("Please enter at least one domain name.");
      const res = await apiRequest(
        "GET",
        `/api/admin/infra/domain-availability?domains=${encodeURIComponent(names.join(","))}`,
      );
      const data = await res.json();
      return (data.availability as Record<string, boolean>) ?? {};
    },
    onSuccess: (av) => setExactAvailability(av),
    onError: (err: any) =>
      toast({
        title: "Couldn't check availability",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  // Lazily fetch a first batch when the tenant lands on the Automatic tab.
  useEffect(() => {
    if (
      surface === "tenant" &&
      domainMode === "automatic" &&
      suggestions.length === 0 &&
      !suggestionsMutation.isPending
    ) {
      suggestionsMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, domainMode]);

  // ── Platform-surface: list pool carousels for lease picker ──────────────────
  // Replaces the raw Maildoso domain list — lease now picks from the platform
  // pre-warmed carousel pool (platform/infra/pool endpoint).
  type PoolCarousel = {
    purchaseId: string;
    domainNames: string[];
    mailboxCount: number;
    mailboxIdentifiers?: string[];
    isAvailable: boolean;
    isWarming: boolean;
    daysWarming: number | null;
    healthScore: number | null;
    lifecycleState: string | null;
    // Task #601 — churned-carousel policy + real graduation signals.
    previouslyLeased?: boolean;
    cleanDays?: number | null;
    capTier?: string | null;
    capPerMailbox?: number | null;
  };
  const platformPoolQuery = useQuery<{ carousels: PoolCarousel[]; available: PoolCarousel[]; warming: PoolCarousel[] }>({
    queryKey: ["/api/platform/infra/pool"],
    enabled: surface === "platform" && mode === "lease",
  });
  const platformPoolCarousels = platformPoolQuery.data?.carousels ?? [];
  const platformAvailableCarousels = platformPoolQuery.data?.available ?? [];
  const [platformSelectedCarousel, setPlatformSelectedCarousel] = useState<string | null>(null);

  // ── Tenant-surface: show available platform pool carousels for lease ─────────
  // Tenants pick from pre-warmed carousels. If none available, show pool status.
  const tenantPoolQuery = useQuery<{ available: PoolCarousel[]; warming: PoolCarousel[]; total: number }>({
    queryKey: ["/api/admin/infra/platform-pool"],
    enabled: surface === "tenant" && mode === "lease",
  });
  const tenantAvailableCarousels = tenantPoolQuery.data?.available ?? [];
  const tenantWarmingCarousels = tenantPoolQuery.data?.warming ?? [];
  const [tenantSelectedCarousel, setTenantSelectedCarousel] = useState<string | null>(null);

  // ── Wizard extras — send-from name, forwarding email, redirect URL ──────────
  const [fromName, setFromName] = useState("");
  const [forwardingEmail, setForwardingEmail] = useState("");
  const [skipForwarding, setSkipForwarding] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");

  // ── Multi-phase provisioning wizard ────────────────────────────────────────
  // Task #601: BOTH buy and lease run through the stepped wizard shell so the
  // dialog never grows past the viewport. Lease has a shorter step list (no
  // mailbox-naming step — leased local-parts are locked to preserve the
  // provider-side warming identity, and no enrollment-observation step — a
  // leased carousel is already enrolled and warmed).
  const [step, setStep] = useState(1);
  // Operator-chosen mailbox local-parts. Length is fixed by the package: this
  // step NAMES the mailboxes, it never changes how many you get.
  const [mailboxNames, setMailboxNames] = useState<string[]>(() =>
    Array(Math.max(0, pkg.mailboxesCount)).fill(""),
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Platform-only: where a freshly built carousel lives. 'unassigned' keeps it
  // in the sandbox; only 'leasable' exposes it to tenants.
  const [poolDisposition, setPoolDisposition] =
    useState<"unassigned" | "leasable" | "platform_use">("unassigned");
  // Set once a platform provision succeeds, so the warmup step can poll it.
  const [provisionedPurchaseId, setProvisionedPurchaseId] = useState<string | null>(null);

  // Platform buy — keyword availability check (uses platform endpoint).
  const platformKeywordCheckMutation = useMutation({
    mutationFn: async () => {
      const seed = platformKeyword.trim();
      if (!seed) throw new Error("Please enter a keyword first.");
      const res = await apiRequest(
        "GET",
        `/api/platform/infra/domain-suggestions?count=${pkg.domainsCount}&keyword=${encodeURIComponent(seed)}`,
      );
      const data = await res.json();
      return (data.suggestions as string[]) ?? [];
    },
    onSuccess: (s) => {
      setPlatformKeywordResults(s);
      setPlatformKeywordChecked(true);
    },
    onError: (err: any) =>
      toast({
        title: "Couldn't check availability",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  // Platform buy — exact name availability check.
  const platformExactCheckMutation = useMutation({
    mutationFn: async () => {
      const names = platformExactNames.map((n) => n.trim().toLowerCase()).filter(Boolean);
      if (names.length === 0) throw new Error("Please enter at least one domain name.");
      const res = await apiRequest(
        "GET",
        `/api/platform/infra/domain-availability?domains=${encodeURIComponent(names.join(","))}`,
      );
      const data = await res.json();
      return (data.availability as Record<string, boolean>) ?? {};
    },
    onSuccess: (av) => setPlatformExactAvail(av),
    onError: (err: any) =>
      toast({
        title: "Couldn't check availability",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  const isFlatBundle = pkg.billingMode === "flat_bundle";
  const totalRetail = isFlatBundle
    ? pkg.flatRateMonthlyRetailCents
    : pkg.domainsCount * pkg.retailCentsPerDomain + pkg.mailboxesCount * pkg.retailCentsPerMailbox;
  const totalWholesale = isFlatBundle
    ? pkg.flatRateMonthlyWholesaleCents
    : pkg.domainsCount * pkg.wholesaleCentsPerDomain + pkg.mailboxesCount * pkg.wholesaleCentsPerMailbox;

  const provisionMutation = useMutation({
    mutationFn: async () => {
      if (surface === "tenant") {
        // Tenant: create a Stripe checkout session carrying confirmed domain names.
        const payload: Record<string, unknown> = { packageId: pkg.id, notes };

        if (mode === "lease") {
          // Lease: domains already in the provider account (platform pool carousel).
          if (!tenantSelectedCarousel) {
            throw new Error("Please select a carousel to lease.");
          }
          if (!fromName.trim()) {
            throw new Error("Please set a send-from name — it's what recipients see on a leased carousel.");
          }
          payload.domainMode = "automatic";
          payload.poolPurchaseId = tenantSelectedCarousel;
        } else {
          // Buy: submit only the operator-confirmed exact list. The submit
          // button is disabled until confirmedDomainNames is non-null, so this
          // guard is a last-resort safety check rather than the primary UX gate.
          if (!confirmedDomainNames || confirmedDomainNames.length !== pkg.domainsCount) {
            throw new Error("Please confirm your domain names before proceeding.");
          }
          payload.domainMode = "exact";
          payload.domainNames = confirmedDomainNames;
        }

        // Wizard extras — send-from display name, reply forwarding, redirect URL.
        if (fromName.trim()) (payload as any).fromName = fromName.trim();
        if (skipForwarding) {
          (payload as any).forwardingEmail = null;
        } else if (forwardingEmail.trim()) {
          (payload as any).forwardingEmail = forwardingEmail.trim();
        }
        if (redirectUrl.trim()) (payload as any).redirectUrl = redirectUrl.trim();
        const chosenMailboxNames = mailboxNames.map((n) => n.trim()).filter(Boolean);
        if (chosenMailboxNames.length) (payload as any).mailboxLocalParts = chosenMailboxNames;

        const res = await apiRequest("POST", "/api/admin/infra/checkout", payload);
        const data = await res.json();
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        }
        return data;
      } else {
        // Platform: direct provision bypass with operator-confirmed domain names.
        const platformPayload: Record<string, unknown> = {
          workspaceId: workspaceIdOverride ?? "",
          packageId: pkg.id,
          domainsCount: pkg.domainsCount,
          mailboxesCount: pkg.mailboxesCount,
          wholesaleCents: totalWholesale,
          retailCents: totalRetail,
          notes,
        };

        if (mode === "lease") {
          // Lease: operator selects from the platform carousel pool.
          if (!platformSelectedCarousel) {
            throw new Error("Please select a carousel from the platform pool.");
          }
          platformPayload.domainMode = "automatic";
          platformPayload.poolPurchaseId = platformSelectedCarousel;
          platformPayload.useExistingDomains = true;
        } else {
          // Buy: submit only the operator-confirmed exact list.
          if (!confirmedDomainNames || confirmedDomainNames.length !== pkg.domainsCount) {
            throw new Error("Please confirm your domain names before proceeding.");
          }
          platformPayload.domainMode = "exact";
          platformPayload.domainNames = confirmedDomainNames;
        }

        // Wizard extras — send-from display name, reply forwarding, redirect URL.
        if (fromName.trim()) (platformPayload as any).fromName = fromName.trim();
        if (skipForwarding) {
          (platformPayload as any).forwardingEmail = null;
        } else if (forwardingEmail.trim()) {
          (platformPayload as any).forwardingEmail = forwardingEmail.trim();
        }
        if (redirectUrl.trim()) (platformPayload as any).redirectUrl = redirectUrl.trim();
        const chosenMailboxNames = mailboxNames.map((n) => n.trim()).filter(Boolean);
        if (chosenMailboxNames.length) (platformPayload as any).mailboxLocalParts = chosenMailboxNames;
        // Disposition only applies to sandbox carousels (no target workspace).
        if (mode === "buy") (platformPayload as any).poolDisposition = poolDisposition;

        const res = await apiRequest("POST", "/api/platform/infra/purchases", platformPayload);
        return res.json();
      }
    },
    onSuccess: (data: any) => {
      if (surface === "platform") {
        toast({ title: "Order placed", description: "Provisioning started." });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/infra/purchases"] });
        if (mode === "buy") {
          // Keep the wizard open so the operator sees warmup enrollment come up
          // live and lands on the send-ramp education screen.
          setProvisionedPurchaseId(data?.purchase?.id ?? data?.purchaseId ?? data?.id ?? null);
          setStep(STEP_KEYS.indexOf("warmup") + 1);
        } else {
          // Lease: the carousel is already enrolled and warmed — nothing to observe.
          onClose();
        }
      }
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message ?? "Failed to place order", variant: "destructive" });
    },
  });

  // ── Wizard step model ──────────────────────────────────────────────────────
  // Steps are addressed by KEY, not number, because lease and buy have
  // different step lists.
  const WIZARD_STEPS = mode === "lease"
    ? ["Choose carousel", "Redirect", "Send-from", "Forwarding", "Warm-up status", "Confirm"]
    : ["Plan & domains", "Redirect", "Mailboxes", "Send-from", "Forwarding", "Warmup", "Confirm"];
  const STEP_KEYS: string[] = mode === "lease"
    ? ["plan", "redirect", "sendfrom", "forwarding", "warmup", "confirm"]
    : ["plan", "redirect", "mailboxes", "sendfrom", "forwarding", "warmup", "confirm"];
  const totalSteps = WIZARD_STEPS.length;
  const stepKey = STEP_KEYS[step - 1];
  const showStep = (k: string) => stepKey === k;
  // Platform BUY provisions at the end of the Forwarding step (there is no
  // payment, so the warmup/confirm steps can show real state). Tenant collects
  // everything first and only hits Stripe on the final step, so an abandoned
  // wizard never leaves a paid-but-unconfigured carousel behind, and the
  // send-ramp education is seen BEFORE any money changes hands. Platform LEASE
  // assigns an already-warmed carousel, so it also provisions on the final step.
  const provisionStep =
    surface === "platform" && mode === "buy"
      ? STEP_KEYS.indexOf("forwarding") + 1
      : totalSteps;

  // The carousel currently selected for lease (either surface), used by the
  // warm-up status + confirm steps.
  const selectedCarousel: PoolCarousel | null =
    mode === "lease"
      ? (surface === "tenant"
          ? tenantAvailableCarousels.find((c) => c.purchaseId === tenantSelectedCarousel)
          : platformPoolCarousels.find((c) => c.purchaseId === platformSelectedCarousel)) ?? null
      : null;
  // Mailboxes are distributed evenly across the confirmed domains, mirroring the
  // provider adapter's distribute() so the wizard previews the REAL addresses
  // rather than a guess.
  // Preview the EXACT addresses the provider will create by running the same
  // shared planner the adapter uses. Reimplementing the distribution or the
  // sanitiser here would let the preview (and the duplicate warning) drift from
  // what actually gets provisioned.
  const mailboxPlan = planMailboxAddresses({
    domainNames:
      confirmedDomainNames && confirmedDomainNames.length > 0
        ? confirmedDomainNames
        : Array.from({ length: Math.max(1, pkg.domainsCount) }, (_, i) => `domain-${i + 1}`),
    mailboxesCount: pkg.mailboxesCount,
    localParts: mailboxNames,
  });
  const mailboxAddresses = mailboxPlan.map((mb) => mb.address);
  const duplicateMailboxAddress = findDuplicateMailboxAddress(mailboxPlan);

  const canAdvance = (k: string): boolean => {
    if (mode === "lease") {
      if (k === "plan") {
        if (!selectedCarousel) return false;
        if (surface === "tenant" && !acceptedTerms) return false;
        if (surface === "platform" && !workspaceIdOverride) return false;
        return true;
      }
      // The send-from name is the ONLY thing carrying the lessee's identity on
      // brand-agnostic leased domains — it is required, not optional.
      if (k === "sendfrom") return fromName.trim().length > 0;
      return true;
    }
    // Two mailboxes on one domain can't share a local-part.
    if (k === "mailboxes") return !duplicateMailboxAddress;
    if (k !== "plan") return true;
    if (!confirmedDomainNames) return false;
    if (surface === "tenant" && !acceptedTerms) return false;
    if (surface === "platform" && !workspaceIdOverride) return false;
    return true;
  };

  // Step 6 (platform): poll the freshly provisioned carousel so Instantly
  // enrollment is OBSERVABLE. Enrollment itself stays automatic — this step
  // never triggers it, it just surfaces success/failure loudly.
  const enrollmentQuery = useQuery<any>({
    queryKey: [`/api/platform/infra/purchases/${provisionedPurchaseId}/items`],
    enabled: surface === "platform" && mode === "buy" && !!provisionedPurchaseId && stepKey === "warmup",
    // Stop polling once nothing more can change — a terminal purchase state, or
    // every expected mailbox has an enrollment outcome.
    refetchInterval: (query) => {
      const d: any = query.state.data;
      if (!d) return 5000;
      const status = d?.purchase?.provisioningStatus;
      if (status === "failed" || status === "cancelled") return false;
      const expected = d?.purchase?.mailboxesCount ?? 0;
      const mbs = (Array.isArray(d) ? d : (d?.items ?? [])).filter(
        (i: any) => i?.itemType === "mailbox",
      );
      const settled =
        expected > 0 &&
        mbs.length >= expected &&
        mbs.every((m: any) => m.instantlyEmail || m.warmupEnrollmentError);
      return settled ? false : 5000;
    },
  });
  const enrollmentItems: any[] = Array.isArray(enrollmentQuery.data)
    ? enrollmentQuery.data
    : (enrollmentQuery.data?.items ?? []);
  const enrollmentMailboxes = enrollmentItems.filter((i) => i?.itemType === "mailbox");
  const enrollmentPurchase = Array.isArray(enrollmentQuery.data)
    ? null
    : (enrollmentQuery.data?.purchase ?? null);

  // Fail loudly instead of spinning forever. Provisioning that has not produced
  // the expected mailboxes within this window is stuck, not slow — a swallowed
  // provider error used to leave this spinner running indefinitely while the
  // purchase sat marked "live" with zero mailboxes.
  const MAILBOX_STALL_MS = 3 * 60_000;
  const [warmupEnteredAt, setWarmupEnteredAt] = useState<number | null>(null);
  useEffect(() => {
    if (stepKey === "warmup" && surface === "platform" && mode === "buy") {
      setWarmupEnteredAt((prev) => prev ?? Date.now());
    }
  }, [stepKey, surface, mode]);

  const expectedMailboxes = enrollmentPurchase?.mailboxesCount ?? pkg.mailboxesCount ?? 0;
  const provisioningFailed =
    enrollmentPurchase?.provisioningStatus === "failed" ||
    enrollmentPurchase?.provisioningStatus === "cancelled";
  const mailboxShortfall = Math.max(0, expectedMailboxes - enrollmentMailboxes.length);
  const mailboxStalled =
    !enrollmentQuery.isLoading &&
    mailboxShortfall > 0 &&
    !!warmupEnteredAt &&
    Date.now() - warmupEnteredAt > MAILBOX_STALL_MS;

  const repairMailboxesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/platform/infra/purchases/${provisionedPurchaseId}/repair-mailboxes`,
        // Explicit operator retry on the purchase they just placed — minting the
        // shortfall is the intent, even if the purchase already flipped to live.
        { force: true },
      );
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data?.satisfied) {
        toast({
          title: "Mailboxes repaired",
          description: `${data.mailboxCount}/${data.expected} mailboxes ready` +
            (data.created ? ` · ${data.created} created` : "") +
            (data.adopted ? ` · ${data.adopted} recovered` : "") +
            (data.enrolled ? ` · ${data.enrolled} enrolled in warmup` : ""),
        });
      } else {
        toast({
          title: "Repair incomplete",
          description:
            data?.error ||
            `Still ${data?.mailboxCount ?? 0}/${data?.expected ?? expectedMailboxes} mailboxes.`,
          variant: "destructive",
        });
      }
      setWarmupEnteredAt(Date.now());
      enrollmentQuery.refetch();
    },
    onError: (err: any) => {
      toast({
        title: "Repair failed",
        description: err?.message || "Could not repair mailboxes.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{surface === "platform" ? "Provision" : mode === "lease" ? "Lease" : "Buy"} {pkg.name}</DialogTitle>
          <DialogDescription>
            {pkg.domainsCount} domains · {pkg.mailboxesCount} mailboxes
          </DialogDescription>
        </DialogHeader>

        {(
          <div className="flex items-center gap-1" data-testid="wizard-stepper">
            {WIZARD_STEPS.map((label, i) => {
              const n = i + 1;
              return (
                <div
                  key={label}
                  title={label}
                  data-testid={`wizard-step-indicator-${n}`}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors",
                    n < step ? "bg-primary" : n === step ? "bg-primary" : "bg-muted",
                    n > step && "opacity-60",
                  )}
                />
              );
            })}
          </div>
        )}
        {(
          <p className="text-xs font-medium text-muted-foreground -mt-1" data-testid="text-wizard-step-label">
            Step {step} of {totalSteps} · {WIZARD_STEPS[step - 1]}
          </p>
        )}

        <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0">
          {surface === "platform" && !workspaceIdOverride && (
            <div className="rounded border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-300">
              Workspace ID required for platform provisioning. Pass it as prop.
            </div>
          )}

          {showStep("plan") && (<>
          <div className="rounded-lg border p-4 space-y-2">
            {isFlatBundle ? (
              <div className="flex justify-between font-semibold text-sm">
                <span>{pkg.domainsCount} domain{pkg.domainsCount !== 1 ? "s" : ""} + {pkg.mailboxesCount} mailbox{pkg.mailboxesCount !== 1 ? "es" : ""}</span>
                <span>{formatCents(totalRetail)} /mn</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Domains ({pkg.domainsCount})</span>
                  <span>{formatCents(pkg.domainsCount * pkg.retailCentsPerDomain)} / {pkg.domainBillingCycle}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Mailboxes ({pkg.mailboxesCount})</span>
                  <span>{formatCents(pkg.mailboxesCount * pkg.retailCentsPerMailbox)} / {pkg.mailboxBillingCycle}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{formatCents(totalRetail)}</span>
                </div>
              </>
            )}
          </div>

          {surface === "platform" && (
            <div className="rounded-lg border p-4 space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between">
                <span>Wholesale</span>
                <span>{formatCents(totalWholesale)}</span>
              </div>
              <div className="flex justify-between text-green-600 dark:text-green-400 font-medium">
                <span>Margin</span>
                <span>{formatCents(totalRetail - totalWholesale)}</span>
              </div>
            </div>
          )}

          {/* ── Platform lease: pick from pre-warmed carousel pool ── */}
          {surface === "platform" && mode === "lease" && (
            <div className="space-y-2">
              <Label>Choose carousel from pool</Label>
              <p className="text-xs text-muted-foreground">
                Assign an available pre-warmed platform carousel to the selected workspace.
              </p>
              {platformPoolQuery.isLoading ? (
                <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground rounded-md border">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading pool…
                </div>
              ) : platformPoolQuery.isError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  Failed to load pool.
                </div>
              ) : platformPoolCarousels.length === 0 ? (
                <div className="rounded-md border p-3 text-xs text-muted-foreground">
                  No carousels in pool yet. Provision some first.
                </div>
              ) : (
                <div className="rounded-md border divide-y max-h-60 overflow-y-auto" data-testid="list-platform-pool-carousels">
                  {platformPoolCarousels.map((c) => {
                    const selected = platformSelectedCarousel === c.purchaseId;
                    return (
                      <button
                        key={c.purchaseId}
                        type="button"
                        disabled={!c.isAvailable}
                        className={cn(
                          "w-full flex items-start justify-between gap-3 p-2.5 text-sm text-left transition-colors",
                          selected
                            ? "bg-primary/10 text-primary"
                            : !c.isAvailable
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-muted/50",
                        )}
                        data-testid={`button-select-carousel-${c.purchaseId}`}
                        onClick={() => {
                          if (!c.isAvailable) return;
                          setPlatformSelectedCarousel(selected ? null : c.purchaseId);
                        }}
                      >
                        <div className="flex items-start gap-2 min-w-0">
                          {selected
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                            : <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.domainNames.slice(0, 2).join(", ")}{c.domainNames.length > 2 ? ` +${c.domainNames.length - 2}` : ""}</div>
                            <div className="text-xs text-muted-foreground">
                              {c.mailboxCount} mailbox{c.mailboxCount !== 1 ? "es" : ""}
                              {c.previouslyLeased ? " · previously leased" : ""}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs shrink-0 text-right">
                          {c.isAvailable ? (
                            <span className="text-green-600 font-medium">Ready</span>
                          ) : c.isWarming ? (
                            <span className="text-amber-600">
                              Warming{c.daysWarming !== null ? ` · ${c.daysWarming}d` : ""}
                              {c.healthScore !== null ? ` · ${c.healthScore}%` : ""}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{c.lifecycleState ?? "unknown"}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {platformAvailableCarousels.length === 0 && platformPoolCarousels.length > 0 && (
                <p className="text-xs text-amber-600">
                  {platformPoolCarousels.length} carousel{platformPoolCarousels.length !== 1 ? "s" : ""} still warming — none ready to assign yet.
                </p>
              )}
            </div>
          )}

          {surface === "platform" && mode === "buy" && (
            <div className="space-y-2">
              <Label>Domain name</Label>
              <Tabs value={platformBuyMode} onValueChange={(v) => setPlatformBuyMode(v as "keyword" | "exact")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="keyword" data-testid="tab-platform-domain-keyword">Keyword</TabsTrigger>
                  <TabsTrigger value="exact" data-testid="tab-platform-domain-exact">Exact name</TabsTrigger>
                </TabsList>

                <TabsContent value="keyword" className="space-y-2 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Enter a keyword and we'll find {pkg.domainsCount} available domain{pkg.domainsCount === 1 ? "" : "s"} based on it.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="input-platform-domain-keyword"
                      value={platformKeyword}
                      onChange={(e) => {
                        setPlatformKeyword(e.target.value);
                        setPlatformKeywordChecked(false);
                        setPlatformKeywordResults([]);
                        setConfirmedDomainNames(null);
                      }}
                      placeholder="e.g. clarik, outreach, sales"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      data-testid="button-platform-keyword-check"
                      disabled={platformKeywordCheckMutation.isPending || !platformKeyword.trim()}
                      onClick={() => platformKeywordCheckMutation.mutate()}
                    >
                      {platformKeywordCheckMutation.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Search className="h-3.5 w-3.5" />}
                      <span className="ml-1.5">Check</span>
                    </Button>
                  </div>
                  {platformKeywordChecked && platformKeywordResults.length > 0 && (
                    <div className="rounded-md border divide-y" data-testid="list-platform-keyword-results">
                      {platformKeywordResults.map((s, i) => (
                        <div key={s} className="flex items-center gap-2 p-2.5 text-sm font-medium" data-testid={`text-platform-keyword-result-${i}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                  {platformKeywordChecked && platformKeywordResults.length === 0 && (
                    <p className="text-xs text-muted-foreground">No available domains found — try a different keyword.</p>
                  )}
                  {platformKeywordChecked && platformKeywordResults.length > 0 && !confirmedDomainNames && (
                    <Button
                      type="button"
                      size="sm"
                      className="w-full"
                      data-testid="button-confirm-platform-keyword-domains"
                      onClick={() => setConfirmedDomainNames([...platformKeywordResults])}
                    >
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                      Confirm these {platformKeywordResults.length} domain{platformKeywordResults.length !== 1 ? "s" : ""}
                    </Button>
                  )}
                  {confirmedDomainNames && platformBuyMode === "keyword" && (
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="text-platform-keyword-confirmed">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {confirmedDomainNames.length} domain{confirmedDomainNames.length !== 1 ? "s" : ""} confirmed — ready to provision
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="exact" className="space-y-2 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Enter the exact domain name{pkg.domainsCount === 1 ? "" : "s"} to register.
                  </p>
                  <div className="space-y-1.5">
                    {platformExactNames.map((name, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          data-testid={`input-platform-exact-${i}`}
                          value={name}
                          onChange={(e) => {
                            const next = [...platformExactNames];
                            next[i] = e.target.value;
                            setPlatformExactNames(next);
                            setPlatformExactAvail({});
                            setConfirmedDomainNames(null);
                          }}
                          placeholder={`domain${i + 1}.com`}
                          className="flex-1"
                        />
                        {name.trim() && platformExactAvail[name.trim().toLowerCase()] !== undefined && (
                          platformExactAvail[name.trim().toLowerCase()]
                            ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" data-testid={`icon-platform-avail-${i}`} />
                            : <XCircle className="h-4 w-4 text-red-500 shrink-0" data-testid={`icon-platform-taken-${i}`} />
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    data-testid="button-platform-exact-check"
                    disabled={platformExactCheckMutation.isPending || !platformExactNames.some((n) => n.trim().length > 0)}
                    onClick={() => platformExactCheckMutation.mutate()}
                  >
                    {platformExactCheckMutation.isPending
                      ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      : <Search className="mr-1.5 h-3.5 w-3.5" />}
                    Check availability
                  </Button>
                  {(() => {
                    const trimmed = platformExactNames.map((n) => n.trim().toLowerCase()).filter(Boolean);
                    const allGreen =
                      trimmed.length === pkg.domainsCount &&
                      trimmed.every((n) => platformExactAvail[n] === true);
                    return allGreen && !confirmedDomainNames ? (
                      <Button
                        type="button"
                        size="sm"
                        className="w-full"
                        data-testid="button-confirm-platform-exact-domains"
                        onClick={() => setConfirmedDomainNames(trimmed)}
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Confirm these {pkg.domainsCount} domain{pkg.domainsCount !== 1 ? "s" : ""}
                      </Button>
                    ) : null;
                  })()}
                  {confirmedDomainNames && platformBuyMode === "exact" && (
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="text-platform-exact-confirmed">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {confirmedDomainNames.length} domain{confirmedDomainNames.length !== 1 ? "s" : ""} confirmed — ready to provision
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}

          {surface === "tenant" && (
            <div className="space-y-2">
              <Label>Your sending carousel</Label>
              {mode === "lease" ? (
                /* Lease: pick from the platform's pre-warmed carousel pool */
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Choose a pre-warmed platform carousel — your domains are already aged and ready to send cold email.
                  </p>
                  {tenantPoolQuery.isLoading ? (
                    <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground rounded-md border">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading available carousels…
                    </div>
                  ) : tenantPoolQuery.isError ? (
                    <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                      Failed to load available carousels.
                    </div>
                  ) : tenantAvailableCarousels.length === 0 ? (
                    <div className="space-y-2">
                      <div className="rounded-md border p-3 text-xs text-muted-foreground">
                        No carousels ready yet — {tenantWarmingCarousels.length > 0
                          ? `${tenantWarmingCarousels.length} warming up, typically ready in ${tenantWarmingCarousels[0]?.daysWarming !== null ? `${Math.max(0, 10 - (tenantWarmingCarousels[0].daysWarming ?? 0))} more days` : "10–14 days"}.`
                          : "check back soon."}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border divide-y max-h-60 overflow-y-auto" data-testid="list-tenant-pool-carousels">
                      {tenantAvailableCarousels.map((c, i) => {
                        const selected = tenantSelectedCarousel === c.purchaseId;
                        return (
                          <button
                            key={c.purchaseId}
                            type="button"
                            className={cn(
                              "w-full flex items-start justify-between gap-3 p-2.5 text-sm text-left transition-colors",
                              selected ? "bg-primary/10 text-primary" : "hover:bg-muted/50",
                            )}
                            data-testid={`button-select-tenant-carousel-${i}`}
                            onClick={() => setTenantSelectedCarousel(selected ? null : c.purchaseId)}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              {selected
                                ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                                : <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />}
                              <div className="min-w-0">
                                <div className="font-medium truncate">
                                  {c.domainNames.slice(0, 2).join(", ")}{c.domainNames.length > 2 ? ` +${c.domainNames.length - 2}` : ""}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {c.mailboxCount} mailbox{c.mailboxCount !== 1 ? "es" : ""}
                                  {c.daysWarming !== null ? ` · warmed ${c.daysWarming} days` : ""}
                                  {c.capTier ? ` · ${c.capTier} tier (${c.capPerMailbox}/day)` : ""}
                                </div>
                                {c.previouslyLeased && (
                                  <Badge variant="outline" className="mt-0.5 text-[10px] px-1.5 py-0 h-4">
                                    Previously leased — cap ramp restarted
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <span className={cn(
                              "text-xs font-medium shrink-0",
                              c.healthScore !== null && c.healthScore >= 80 ? "text-green-600" : "text-amber-600",
                            )}>
                              {c.healthScore !== null ? `${c.healthScore}%` : "Ready"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {tenantWarmingCarousels.length > 0 && tenantAvailableCarousels.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {tenantWarmingCarousels.length} more carousel{tenantWarmingCarousels.length !== 1 ? "s" : ""} warming up.
                    </p>
                  )}
                </div>
              ) : (
                /* Buy: all three tabs — Automatic, Keyword, Exact */
                <Tabs value={domainMode} onValueChange={(v) => setDomainMode(v as typeof domainMode)}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="automatic" data-testid="tab-domain-automatic">Automatic</TabsTrigger>
                    <TabsTrigger value="keyword" data-testid="tab-domain-keyword">Keyword</TabsTrigger>
                    <TabsTrigger value="exact" data-testid="tab-domain-exact">Exact</TabsTrigger>
                  </TabsList>

                  {/* Automatic tab */}
                  <TabsContent value="automatic" className="space-y-2 pt-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground">
                        We'll register {pkg.domainsCount} neutral, ready-to-send domain
                        {pkg.domainsCount === 1 ? "" : "s"} for you — no branding needed.
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 shrink-0 px-2 text-xs"
                        data-testid="button-regenerate-domains"
                        disabled={suggestionsMutation.isPending}
                        onClick={() => { setConfirmedDomainNames(null); suggestionsMutation.mutate(); }}
                      >
                        <RefreshCw
                          className={cn(
                            "mr-1 h-3 w-3",
                            suggestionsMutation.isPending && "animate-spin",
                          )}
                        />
                        Regenerate
                      </Button>
                    </div>
                    <div className="rounded-md border divide-y" data-testid="list-domain-suggestions">
                      {suggestionsMutation.isPending && suggestions.length === 0 ? (
                        <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Finding available domains…
                        </div>
                      ) : suggestions.length === 0 ? (
                        <div className="p-3 text-xs text-muted-foreground">
                          No suggestions yet — tap Regenerate to find available domains.
                        </div>
                      ) : (
                        suggestions.map((s, i) => (
                          <div
                            key={s}
                            className="flex items-center gap-2 p-2.5 text-sm font-medium"
                            data-testid={`text-domain-suggestion-${i}`}
                          >
                            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                            {s}
                          </div>
                        ))
                      )}
                    </div>
                    {suggestions.length === pkg.domainsCount && !confirmedDomainNames && (
                      <Button
                        type="button"
                        size="sm"
                        className="w-full"
                        data-testid="button-confirm-automatic-domains"
                        onClick={() => setConfirmedDomainNames([...suggestions])}
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Confirm these {pkg.domainsCount} domain{pkg.domainsCount !== 1 ? "s" : ""}
                      </Button>
                    )}
                    {confirmedDomainNames && domainMode === "automatic" && (
                      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="text-automatic-domains-confirmed">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {confirmedDomainNames.length} domain{confirmedDomainNames.length !== 1 ? "s" : ""} confirmed — ready to proceed
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Want a specific name?{" "}
                      <button
                        type="button"
                        className="font-medium text-primary underline-offset-2 hover:underline"
                        data-testid="link-switch-to-custom"
                        onClick={() => { setDomainMode("keyword"); setConfirmedDomainNames(null); }}
                      >
                        Use a keyword instead
                      </button>
                      .
                    </p>
                  </TabsContent>

                  {/* Keyword tab */}
                  <TabsContent value="keyword" className="space-y-2 pt-2">
                    <p className="text-xs text-muted-foreground">
                      Give us a word and we'll find {pkg.domainsCount} available domain
                      {pkg.domainsCount === 1 ? "" : "s"} based on it.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        data-testid="input-domain-keyword"
                        value={domainKeyword}
                        onChange={(e) => {
                          setDomainKeyword(e.target.value);
                          setKeywordChecked(false);
                          setKeywordSuggestions([]);
                          setConfirmedDomainNames(null);
                        }}
                        placeholder="e.g. brightreach"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        data-testid="button-keyword-check-availability"
                        disabled={keywordCheckMutation.isPending || !domainKeyword.trim()}
                        onClick={() => keywordCheckMutation.mutate()}
                      >
                        {keywordCheckMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Search className="h-3.5 w-3.5" />
                        )}
                        <span className="ml-1.5">Check availability</span>
                      </Button>
                    </div>
                    {keywordChecked && keywordSuggestions.length > 0 && (
                      <div className="rounded-md border divide-y" data-testid="list-keyword-suggestions">
                        {keywordSuggestions.map((s, i) => (
                          <div
                            key={s}
                            className="flex items-center gap-2 p-2.5 text-sm font-medium"
                            data-testid={`text-keyword-suggestion-${i}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                    {keywordChecked && keywordSuggestions.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No available domains found for that keyword — try a different word.
                      </p>
                    )}
                    {keywordChecked && keywordSuggestions.length > 0 && !confirmedDomainNames && (
                      <Button
                        type="button"
                        size="sm"
                        className="w-full"
                        data-testid="button-confirm-keyword-domains"
                        onClick={() => setConfirmedDomainNames([...keywordSuggestions])}
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Confirm these {keywordSuggestions.length} domain{keywordSuggestions.length !== 1 ? "s" : ""}
                      </Button>
                    )}
                    {confirmedDomainNames && domainMode === "keyword" && (
                      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="text-keyword-domains-confirmed">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {confirmedDomainNames.length} domain{confirmedDomainNames.length !== 1 ? "s" : ""} confirmed — ready to proceed
                      </p>
                    )}
                  </TabsContent>

                  {/* Exact tab */}
                  <TabsContent value="exact" className="space-y-2 pt-2">
                    <p className="text-xs text-muted-foreground">
                      Enter exactly {pkg.domainsCount} domain name{pkg.domainsCount === 1 ? "" : "s"} you'd
                      like us to register.
                    </p>
                    <div className="space-y-1.5">
                      {exactNames.map((name, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            data-testid={`input-domain-exact-${i}`}
                            value={name}
                            onChange={(e) => {
                              const next = [...exactNames];
                              next[i] = e.target.value;
                              setExactNames(next);
                              setExactAvailability({});
                              setConfirmedDomainNames(null);
                            }}
                            placeholder={`domain${i + 1}.com`}
                            className="flex-1"
                          />
                          {name.trim() && exactAvailability[name.trim().toLowerCase()] !== undefined && (
                            exactAvailability[name.trim().toLowerCase()] ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" data-testid={`icon-exact-available-${i}`} />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 shrink-0" data-testid={`icon-exact-taken-${i}`} />
                            )
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      data-testid="button-exact-check-availability"
                      disabled={
                        exactCheckMutation.isPending ||
                        !exactNames.some((n) => n.trim().length > 0)
                      }
                      onClick={() => exactCheckMutation.mutate()}
                    >
                      {exactCheckMutation.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Search className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Check availability
                    </Button>
                    {Object.keys(exactAvailability).length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        <CheckCircle2 className="inline h-3 w-3 text-green-500 mr-1" />available &nbsp;
                        <XCircle className="inline h-3 w-3 text-red-500 mr-1" />taken
                      </p>
                    )}
                    {(() => {
                      const trimmed = exactNames.map((n) => n.trim().toLowerCase()).filter(Boolean);
                      const allGreen =
                        trimmed.length === pkg.domainsCount &&
                        trimmed.every((n) => exactAvailability[n] === true);
                      return allGreen && !confirmedDomainNames ? (
                        <Button
                          type="button"
                          size="sm"
                          className="w-full"
                          data-testid="button-confirm-exact-domains"
                          onClick={() => setConfirmedDomainNames(trimmed)}
                        >
                          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                          Confirm these {pkg.domainsCount} domain{pkg.domainsCount !== 1 ? "s" : ""}
                        </Button>
                      ) : null;
                    })()}
                    {confirmedDomainNames && domainMode === "exact" && (
                      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="text-exact-domains-confirmed">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {confirmedDomainNames.length} domain{confirmedDomainNames.length !== 1 ? "s" : ""} confirmed — ready to proceed
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </div>
          )}

          {/* ── Platform: where does this carousel live once built? ──────────── */}
          {mode === "buy" && surface === "platform" && (
            <div className="space-y-2 rounded-lg border p-3">
              <Label>Once built, this carousel is…</Label>
              <div className="space-y-1.5">
                {([
                  ["unassigned", "Sandbox", "Warms up privately. Not offered to tenants."],
                  ["leasable", "Available to lease", "Listed for tenants once it finishes warming."],
                  ["platform_use", "Reserved for Clarik", "For our own outreach. Never offered to tenants."],
                ] as const).map(([value, label, help]) => (
                  <label
                    key={value}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                    data-testid={`radio-pool-disposition-${value}`}
                  >
                    <input
                      type="radio"
                      name="pool-disposition"
                      className="mt-1 h-3.5 w-3.5 accent-primary"
                      checked={poolDisposition === value}
                      onChange={() => setPoolDisposition(value)}
                    />
                    <span>
                      <span className="font-medium">{label}</span>
                      <span className="block text-xs text-muted-foreground">{help}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Tenant: non-refundable terms gate before any charge ──────────── */}
          {surface === "tenant" && (
            <div className="flex items-start gap-2 rounded-lg border p-3">
              <input
                type="checkbox"
                id="infra-accept-terms"
                data-testid="checkbox-infra-accept-terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              />
              <label htmlFor="infra-accept-terms" className="text-xs cursor-pointer select-none">
                {mode === "lease" ? (
                  <>
                    I understand this lease is <strong>non-refundable</strong>, that mailbox
                    addresses on a leased carousel are fixed (renaming a warmed mailbox
                    destroys its warming identity), and that daily send caps ramp up
                    gradually based on the carousel's reputation.
                  </>
                ) : (
                  <>
                    I understand these domains are registered specifically for me and this
                    purchase is <strong>non-refundable</strong>, and that new mailboxes cannot
                    send prospect email until they finish warming (about 10 days).
                  </>
                )}
              </label>
            </div>
          )}
          </>)}

          {/* ── Send-ramp education — shown on the Confirm step in both modes. ── */}
          {showStep("confirm") && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300 space-y-2">
              <p className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Prospect send ramp — operator awareness
              </p>
              <p>
                New mailboxes enter a <strong>seasoning period (~10+ days)</strong>: Instantly warmup
                network conditioning + inbox-placement tests must both pass before any prospect sends
                are allowed. <strong>Zero prospect sends during seasoning — enforced automatically.</strong>
              </p>
              <div className="rounded border border-amber-200 dark:border-amber-700 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-amber-200 dark:border-amber-700 bg-amber-100/60 dark:bg-amber-900/30">
                      <th className="text-left py-1 px-2 font-medium">Stage (after seasoning)</th>
                      <th className="text-right py-1 px-2 font-medium">Per mailbox / day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COLD_SEND_TIER_GUIDE.map((entry) => (
                      <tr
                        key={entry.tier}
                        className="border-b border-amber-100 dark:border-amber-800/50 last:border-0"
                        data-testid={`row-platform-prospect-${entry.tier}`}
                      >
                        <td className="py-1 px-2">
                          {entry.label}{" "}
                          <span className="opacity-70">({entry.dayRange})</span>
                        </td>
                        <td className="text-right py-1 px-2 font-semibold">
                          {entry.cap} prospects/day
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="opacity-80">
                Placement tests ({PLACEMENT_TEST_DAILY_BUDGET}/mailbox/day) run during seasoning
                and do not count against the prospect cap.
              </p>
            </div>
          )}

          {/* ── Send-from display name ────────────────────────────────────────── */}
          {showStep("sendfrom") && (
          <div className="space-y-2">
            <div className="space-y-1">
              <Label htmlFor="infra-from-name">
                Send-from name{" "}
                {mode === "lease" ? (
                  <span className="text-destructive font-normal">(required)</span>
                ) : (
                  <span className="text-muted-foreground font-normal">(optional)</span>
                )}
              </Label>
              <Input
                id="infra-from-name"
                data-testid="input-infra-from-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="e.g. The Booking Team"
                maxLength={255}
              />
              <p className="text-xs text-muted-foreground">
                {mode === "lease"
                  ? "Leased domains are deliberately brand-agnostic, so this display name is the ONLY thing that carries your identity to recipients. Without it, your cold email would go out under a generic name."
                  : "Display name shown to recipients. Leave blank to use your workspace name."}
              </p>
            </div>
            {mode === "lease" && selectedCarousel && (
              <div className="rounded-md border p-3 space-y-1.5" data-testid="panel-locked-mailboxes">
                <p className="text-xs font-medium">Mailbox addresses (locked)</p>
                <div className="space-y-0.5">
                  {(selectedCarousel.mailboxIdentifiers ?? []).map((addr) => (
                    <p key={addr} className="text-xs font-mono text-muted-foreground truncate">{addr}</p>
                  ))}
                  {(selectedCarousel.mailboxIdentifiers ?? []).length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedCarousel.mailboxCount} pre-warmed mailbox{selectedCarousel.mailboxCount !== 1 ? "es" : ""}
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  These addresses can't be renamed — a warmed mailbox's address IS its
                  reputation. Renaming would destroy the warming you're leasing.
                </p>
              </div>
            )}
          </div>
          )}

          {/* ── Cold-reply forwarding ─────────────────────────────────────────── */}
          {showStep("forwarding") && (
          <div className="space-y-2">
            <Label>
              Reply forwarding{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="infra-skip-forwarding"
                data-testid="checkbox-infra-skip-forwarding"
                checked={skipForwarding}
                onChange={(e) => {
                  setSkipForwarding(e.target.checked);
                  if (e.target.checked) setForwardingEmail("");
                }}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <label htmlFor="infra-skip-forwarding" className="text-sm cursor-pointer select-none">
                Replies handled only in unified inbox (no external forwarding)
              </label>
            </div>
            {!skipForwarding && (
              <Input
                id="infra-forwarding-email"
                data-testid="input-infra-forwarding-email"
                value={forwardingEmail}
                onChange={(e) => setForwardingEmail(e.target.value)}
                placeholder="replies@yourcompany.com, info@yourcompany.com"
                // Not type="email" — that blocks the comma-separated list the
                // field accepts, so the browser silently refuses valid input.
                type="text"
              />
            )}
            <p className="text-xs text-muted-foreground">
              {skipForwarding
                ? "Cold replies will appear only in the Clarik unified inbox."
                : "Cold replies will be forwarded here AND appear in the unified inbox. Separate multiple addresses with commas."}
            </p>
          </div>
          )}

          {/* ── Domain redirect URL ───────────────────────────────────────────── */}
          {showStep("redirect") && (
          <div className="space-y-1">
            <Label htmlFor="infra-redirect-url">
              Domain redirect URL{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="infra-redirect-url"
              data-testid="input-infra-redirect-url"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              type="url"
            />
            <p className="text-xs text-muted-foreground">
              Where visitors who type a cold domain into a browser are redirected. Defaults
              to your workspace website if left blank.
            </p>
          </div>
          )}

          {showStep("forwarding") && (
          <div className="space-y-1">
            <Label htmlFor="infra-notes">Notes (optional)</Label>
            <VoiceToTextTextarea
              id="infra-notes"
              data-testid="input-infra-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes…"
              rows={2}
            />
          </div>
          )}

          {/* ── Buy: name the mailboxes (lease local-parts are locked) ────────── */}
          {mode === "buy" && showStep("mailboxes") && (
            <div className="space-y-3">
              <div>
                <Label>Name your mailboxes</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {pkg.mailboxesCount} mailboxes across {pkg.domainsCount}{" "}
                  {pkg.domainsCount === 1 ? "domain" : "domains"} — the count is fixed by
                  this package. Leave a field blank to use the suggested name.
                </p>
              </div>
              <div className="space-y-2">
                {mailboxPlan.map((mb, i) => (
                  <div key={`${mb.domain}-${i}`} className="flex items-center gap-1.5">
                    <Input
                      value={mailboxNames[i] ?? ""}
                      data-testid={`input-mailbox-name-${i}`}
                      onChange={(e) => {
                        // Only strip characters the provider can never accept.
                        // Leading/trailing dots and dashes are left alone while
                        // typing (removing them mid-keystroke makes the field
                        // feel broken) — the shared sanitiser trims them when
                        // the address is resolved.
                        const cleaned = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9._-]/g, "")
                          .slice(0, 64);
                        setMailboxNames((prev) => {
                          const next = [...prev];
                          next[i] = cleaned;
                          return next;
                        });
                      }}
                      placeholder={mb.fallbackLocalPart}
                      maxLength={64}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[45%]">
                      @{mb.domain}
                    </span>
                  </div>
                ))}
              </div>
              {duplicateMailboxAddress && (
                <p className="text-xs text-destructive" data-testid="text-mailbox-duplicate">
                  Two mailboxes would both be {duplicateMailboxAddress}. Give them
                  different names.
                </p>
              )}
            </div>
          )}

          {/* ── Buy: warmup enrollment observation ────────────────────────────── */}
          {mode === "buy" && showStep("warmup") && (
            <div className="space-y-3">
              <div>
                <Label>Warmup enrollment</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Every new mailbox is enrolled in warmup automatically — there is
                  nothing to click here. Warmup builds sending reputation so your
                  prospect email lands in the inbox instead of spam.
                </p>
              </div>

              {surface === "tenant" ? (
                <div className="rounded-md border p-3 text-xs space-y-1.5">
                  <p className="font-medium">What happens after checkout</p>
                  <p className="text-muted-foreground">
                    We register your domains, create your mailboxes, and enroll them in
                    warmup within a few minutes. You'll see live health scores on the
                    Sending Infrastructure page.
                  </p>
                </div>
              ) : enrollmentQuery.isLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking enrollment…
                </div>
              ) : provisioningFailed || mailboxStalled ? (
                <div
                  className="rounded-md border border-destructive/50 bg-destructive/5 p-3 space-y-2"
                  data-testid="alert-mailbox-provisioning-stalled"
                >
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-destructive">
                        {provisioningFailed
                          ? "Provisioning failed"
                          : `Mailbox provisioning is stuck — ${enrollmentMailboxes.length} of ${expectedMailboxes} created`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {enrollmentPurchase?.notes ||
                          "The domains registered, but the mailbox step did not finish. This does not resolve on its own — retry it below."}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="w-full"
                    data-testid="button-repair-mailboxes"
                    disabled={repairMailboxesMutation.isPending}
                    onClick={() => repairMailboxesMutation.mutate()}
                  >
                    {repairMailboxesMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Repairing…
                      </>
                    ) : (
                      `Retry mailbox provisioning${mailboxShortfall > 0 ? ` (${mailboxShortfall} missing)` : ""}`
                    )}
                  </Button>
                </div>
              ) : enrollmentMailboxes.length === 0 ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Provisioning mailboxes…
                  this can take a few minutes.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {enrollmentMailboxes.map((mb: any) => {
                    const enrolled = !!mb.instantlyEmail;
                    const failed = !!mb.warmupEnrollmentError;
                    return (
                      <div
                        key={mb.id}
                        data-testid={`row-enrollment-${mb.id}`}
                        className="flex items-start justify-between gap-2 rounded border p-2 text-xs"
                      >
                        <span className="truncate font-mono">{mb.identifier}</span>
                        {failed ? (
                          <span className="text-destructive shrink-0 text-right">
                            Enrollment failed
                            <span className="block font-normal opacity-80">
                              {String(mb.warmupEnrollmentError).slice(0, 80)}
                            </span>
                          </span>
                        ) : enrolled ? (
                          <span className="text-green-600 dark:text-green-500 shrink-0">
                            Enrolled
                          </span>
                        ) : (
                          <span className="text-muted-foreground shrink-0">Pending…</span>
                        )}
                      </div>
                    );
                  })}
                  {enrollmentMailboxes.some((m: any) => m.warmupEnrollmentError) && (
                    <p className="text-xs text-muted-foreground">
                      Failed enrollments are retried automatically every hour. No action
                      needed unless the error persists.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Lease: warm-up status — the REAL graduation signals ───────────── */}
          {mode === "lease" && showStep("warmup") && (
            <div className="space-y-3" data-testid="panel-lease-warmup-status">
              <div>
                <Label>Warm-up status</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  A mailbox may only send prospect email once it passes BOTH graduation
                  checks below. Age alone doesn't make a mailbox ready.
                </p>
              </div>
              {!selectedCarousel ? (
                <div className="rounded-md border p-3 text-xs text-muted-foreground">
                  Select a carousel first.
                </div>
              ) : (
                <>
                  <div className="rounded-md border divide-y text-xs">
                    <div className="flex items-center justify-between gap-2 p-2.5" data-testid="row-gate-health">
                      <span>
                        Warmup health score{" "}
                        <span className="text-muted-foreground">(gate: ≥ {WARMUP_GRADUATION_GATE.minHealthScore})</span>
                      </span>
                      <span className="flex items-center gap-1.5 font-medium shrink-0">
                        {selectedCarousel.healthScore ?? "—"}
                        {selectedCarousel.healthScore !== null &&
                          (selectedCarousel.healthScore >= WARMUP_GRADUATION_GATE.minHealthScore
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            : <XCircle className="h-3.5 w-3.5 text-amber-500" />)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 p-2.5" data-testid="row-gate-days">
                      <span>
                        Days in warmup{" "}
                        <span className="text-muted-foreground">(gate: ≥ {WARMUP_GRADUATION_GATE.minDays} days)</span>
                      </span>
                      <span className="flex items-center gap-1.5 font-medium shrink-0">
                        {selectedCarousel.daysWarming ?? "—"}
                        {selectedCarousel.daysWarming !== null &&
                          (selectedCarousel.daysWarming >= WARMUP_GRADUATION_GATE.minDays
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            : <XCircle className="h-3.5 w-3.5 text-amber-500" />)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 p-2.5" data-testid="row-current-cap-tier">
                      <span>Current daily cap tier</span>
                      <span className="font-medium shrink-0">
                        {selectedCarousel.capTier
                          ? `${selectedCarousel.capTier} — ${selectedCarousel.capPerMailbox} emails/mailbox/day`
                          : "—"}
                      </span>
                    </div>
                  </div>
                  {selectedCarousel.previouslyLeased && (
                    <div
                      className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300 space-y-1"
                      data-testid="banner-previously-leased"
                    >
                      <p className="font-semibold flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        Previously leased carousel
                      </p>
                      <p>
                        This carousel was returned to the pool by a previous lessee. It has
                        been re-warmed and re-verified before being offered again, and its
                        daily send caps <strong>restarted at the "{COLD_SEND_TIER_GUIDE[0].label}" tier
                        ({COLD_SEND_TIER_GUIDE[0].cap}/mailbox/day)</strong> from the day it
                        came back — the ramp does not carry over from the prior lease.
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Caps ramp with clean sending age ({COLD_SEND_TIER_GUIDE.map((e) => e.cap).join(" → ")}/day
                    per mailbox) and are scaled down automatically if reputation dips.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Lease: confirm recap ──────────────────────────────────────────── */}
          {mode === "lease" && showStep("confirm") && (
            <div className="rounded-md border p-3 text-xs space-y-1.5" data-testid="panel-lease-recap">
              <p className="font-medium">Your lease</p>
              <div className="text-muted-foreground space-y-1">
                <p>
                  <span className="font-medium text-foreground">Carousel:</span>{" "}
                  {selectedCarousel ? selectedCarousel.domainNames.join(", ") : "—"}
                </p>
                <p>
                  <span className="font-medium text-foreground">Mailboxes:</span>{" "}
                  {selectedCarousel
                    ? (selectedCarousel.mailboxIdentifiers?.length
                        ? selectedCarousel.mailboxIdentifiers.join(", ")
                        : `${selectedCarousel.mailboxCount} pre-warmed`) + " (addresses locked)"
                    : "—"}
                </p>
                <p>
                  <span className="font-medium text-foreground">Send-from name:</span>{" "}
                  {fromName.trim() || "—"}
                </p>
                <p>
                  <span className="font-medium text-foreground">Replies:</span>{" "}
                  {skipForwarding
                    ? "Unified inbox only"
                    : forwardingEmail.trim()
                      ? `Forwarded to ${forwardingEmail.trim()}`
                      : "Unified inbox only"}
                </p>
                {selectedCarousel?.previouslyLeased && (
                  <p>
                    <span className="font-medium text-foreground">Note:</span>{" "}
                    Previously leased — send caps restart at the {COLD_SEND_TIER_GUIDE[0].label} tier.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Buy: confirm recap (the send-ramp table renders above) ────────── */}
          {mode === "buy" && showStep("confirm") && (
            <div className="rounded-md border p-3 text-xs space-y-1.5">
              <p className="font-medium">Your setup</p>
              <div className="text-muted-foreground space-y-1">
                <p>
                  <span className="font-medium text-foreground">Domains:</span>{" "}
                  {(confirmedDomainNames ?? []).join(", ") || "—"}
                </p>
                <p>
                  <span className="font-medium text-foreground">Mailboxes:</span>{" "}
                  {mailboxAddresses.join(", ")}
                </p>
                <p>
                  <span className="font-medium text-foreground">Send-from name:</span>{" "}
                  {fromName.trim() || "Your workspace name"}
                </p>
                <p>
                  <span className="font-medium text-foreground">Replies:</span>{" "}
                  {skipForwarding
                    ? "Unified inbox only"
                    : forwardingEmail.trim()
                      ? `Forwarded to ${forwardingEmail.trim()}`
                      : "Unified inbox only"}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            data-testid="button-wizard-back"
            onClick={() => (step === 1 ? onClose() : setStep((s) => s - 1))}
            disabled={
              provisionMutation.isPending ||
              // Past the provisioning step there is nothing left to edit.
              step > provisionStep
            }
          >
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step === provisionStep ? (
            <Button
              data-testid="button-infra-confirm-buy"
              onClick={() => provisionMutation.mutate()}
              disabled={
                provisionMutation.isPending ||
                (surface === "platform" && !workspaceIdOverride) ||
                (mode === "lease"
                  ? !selectedCarousel ||
                    !fromName.trim() ||
                    (surface === "tenant" && !acceptedTerms)
                  : !confirmedDomainNames ||
                    !!duplicateMailboxAddress ||
                    (surface === "tenant" && !acceptedTerms))
              }
            >
              {provisionMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
              ) : surface === "tenant" ? (
                <><ShoppingCart className="mr-2 h-4 w-4" /> Proceed to Checkout</>
              ) : (
                <><Server className="mr-2 h-4 w-4" /> {mode === "lease" ? "Assign Carousel" : "Provision Now"}</>
              )}
            </Button>
          ) : step === totalSteps ? (
            <Button data-testid="button-wizard-done" onClick={onClose}>
              Done
            </Button>
          ) : (
            <Button
              data-testid="button-wizard-next"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance(stepKey)}
            >
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Generator settings card (platform-only, Task #450) ──────────────────────

function GeneratorSettingsCard() {
  const { toast } = useToast();
  const { data: config, isLoading } = useQuery<{ preferredTld: string; wordHints: string[] }>({
    queryKey: ["/api/platform/infra/generator-config"],
  });

  const [tld, setTld] = useState<string>("com");
  const [hintsInput, setHintsInput] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config && !dirty) {
      setTld(config.preferredTld ?? "com");
      setHintsInput((config.wordHints ?? []).join(", "));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const wordHints = hintsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await apiRequest("PUT", "/api/platform/infra/generator-config", {
        preferredTld: tld,
        wordHints,
      });
      return res.json();
    },
    onSuccess: (saved: any) => {
      toast({ title: "Generator settings saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/infra/generator-config"] });
      setTld(saved.preferredTld ?? "com");
      setHintsInput((saved.wordHints ?? []).join(", "));
      setDirty(false);
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message, variant: "destructive" }),
  });

  return (
    <Card data-testid="card-generator-settings">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          Automatic Generator Settings
        </CardTitle>
        <CardDescription className="text-xs">
          Controls how brand-agnostic domain names are generated for tenant lease packages.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Preferred TLD</Label>
                <Select
                  value={tld}
                  onValueChange={(v) => { setTld(v); setDirty(true); }}
                >
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid="select-generator-tld"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="com">.com</SelectItem>
                    <SelectItem value="net">.net</SelectItem>
                    <SelectItem value="org">.org</SelectItem>
                    <SelectItem value="co">.co</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Generator tries this TLD first for every name.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Word hints (optional)</Label>
                <Input
                  className="h-8 text-xs"
                  data-testid="input-generator-word-hints"
                  placeholder="e.g. reach, nova, pulse"
                  value={hintsInput}
                  onChange={(e) => { setHintsInput(e.target.value); setDirty(true); }}
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated seeds blended into generation.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              data-testid="button-save-generator-settings"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Save settings
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Package card ─────────────────────────────────────────────────────────────

function PackageCard({
  pkg,
  mode,
  surface,
  workspaceIdOverride,
  onEdit,
  onDelete,
}: {
  pkg: InfraPackage;
  mode: "lease" | "buy";
  surface: "tenant" | "platform";
  workspaceIdOverride?: string;
  onEdit?: (pkg: InfraPackage) => void;
  onDelete?: (id: string) => void;
}) {
  // All surfaces/modes go straight to BuyDialog — the wizard's own Confirm
  // step carries the send-ramp disclosure for tenant buy mode (Task #609).
  const [buying, setBuying] = useState(false);

  function handleBuyClick() {
    setBuying(true);
  }

  const isFlatBundle = pkg.billingMode === "flat_bundle";
  const totalRetail = isFlatBundle
    ? pkg.flatRateMonthlyRetailCents
    : pkg.domainsCount * pkg.retailCentsPerDomain + pkg.mailboxesCount * pkg.retailCentsPerMailbox;
  const totalWholesale = isFlatBundle
    ? pkg.flatRateMonthlyWholesaleCents
    : pkg.domainsCount * pkg.wholesaleCentsPerDomain + pkg.mailboxesCount * pkg.wholesaleCentsPerMailbox;

  return (
    <>
      <Card
        className={cn(
          "relative flex flex-col transition-shadow hover:shadow-md",
          !pkg.isActive && "opacity-60",
        )}
        data-testid={`card-infra-package-${pkg.id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">{pkg.name}</CardTitle>
              {pkg.description && (
                <CardDescription className="mt-0.5 text-xs">{pkg.description}</CardDescription>
              )}
            </div>
            {surface === "platform" && onEdit && onDelete && (
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(pkg)} data-testid={`button-edit-pkg-${pkg.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(pkg.id)} data-testid={`button-delete-pkg-${pkg.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex flex-col gap-3 flex-1">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{pkg.domainsCount} domain{pkg.domainsCount !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MailCheck className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{pkg.mailboxesCount} mailbox{pkg.mailboxesCount !== 1 ? "es" : ""}</span>
            </div>
          </div>

          <Separator />

          <div className="text-sm space-y-1">
            {surface === "tenant" ? (
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold">{formatCents(totalRetail)}</span>
                <span className="text-muted-foreground text-xs">/mn</span>
              </div>
            ) : (
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Retail</span>
                  <span className="font-medium text-foreground">{formatCents(totalRetail)}{isFlatBundle ? " /mn" : ""}</span>
                </div>
                <div className="flex justify-between">
                  <span>Wholesale</span>
                  <span>{formatCents(totalWholesale)}</span>
                </div>
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>Margin</span>
                  <span>{formatCents(totalRetail - totalWholesale)}</span>
                </div>
              </div>
            )}
          </div>

          <Button
            className="mt-auto w-full"
            size="sm"
            disabled={!pkg.isActive}
            onClick={handleBuyClick}
            data-testid={`button-buy-package-${pkg.id}`}
          >
            <ShoppingCart className="mr-2 h-4 w-4" />
            {surface === "tenant" ? (mode === "lease" ? "Lease" : "Buy") : "Provision"}
          </Button>
        </CardContent>
      </Card>

      {buying && (
        <BuyDialog
          pkg={pkg}
          mode={mode}
          surface={surface}
          workspaceIdOverride={workspaceIdOverride}
          onClose={() => setBuying(false)}
        />
      )}
    </>
  );
}

// ─── Purchase row ─────────────────────────────────────────────────────────────

// Platform sentinel workspace that owns pool carousels (mirrors the server's
// PLATFORM_COLD_WORKSPACE_ID). Disposition is only editable while a carousel
// is still sentinel-owned — once leased to a tenant the server rejects changes.
const PLATFORM_COLD_SENTINEL = "__platform_cold__";

const DISPOSITION_META: Record<string, { label: string; className: string }> = {
  unassigned: { label: "Sandbox", className: "bg-muted text-muted-foreground" },
  leasable: { label: "Leasable", className: "bg-green-500/15 text-green-700 dark:text-green-400" },
  platform_use: { label: "Reserved", className: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
};

function PurchaseRow({
  purchase,
  onCancel,
  onReplaceItem,
  onChangeDisposition,
  onEditFromName,
}: {
  purchase: InfraPurchase;
  onCancel: (id: string) => void;
  onReplaceItem: (itemId: string) => void;
  /** Platform surface only — change a sentinel-owned carousel's disposition. */
  onChangeDisposition?: (id: string, poolDisposition: string) => void;
  /** Tenant surface — edit the send-from display name on a live leased purchase. */
  onEditFromName?: (id: string, current: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate warm-up capacity across all live mailboxes in this purchase
  const liveMailboxes = (purchase.items ?? []).filter(
    (it) => it.itemType === "mailbox" && it.status === "live",
  );
  let warmupSummary: string | null = null;
  if (liveMailboxes.length > 0) {
    const caps = liveMailboxes.map((it) => getWarmupInfo(it.createdAt).currentCap);
    const allFullyRamped = caps.every((c) => c === null);
    if (allFullyRamped) {
      warmupSummary = "Fully ramped";
    } else {
      const totalCap = caps.reduce<number>((sum, c) => sum + (c ?? 0), 0);
      warmupSummary = `Currently sending up to ${totalCap.toLocaleString()} emails/day across ${liveMailboxes.length} mailbox${liveMailboxes.length === 1 ? "" : "es"}`;
    }
  }

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`row-purchase-${purchase.id}`}
      >
        <TableCell className="font-mono text-xs">{purchase.id.slice(0, 8)}…</TableCell>
        <TableCell>
          <div>{statusBadge(purchase.provisioningStatus)}</div>
          {(purchase.provisioningStatus === "failed") && purchase.notes && (
            <div
              className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-[180px] truncate"
              title={purchase.notes}
              data-testid={`text-purchase-fail-reason-${purchase.id}`}
            >
              {purchase.notes}
            </div>
          )}
        </TableCell>
        <TableCell>
          <div>{purchase.domainsCount}d / {purchase.mailboxesCount}mb</div>
          {warmupSummary && (
            <div
              className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"
              data-testid={`text-warmup-summary-${purchase.id}`}
            >
              <TrendingUp className="h-3 w-3 shrink-0" />
              {warmupSummary}
            </div>
          )}
        </TableCell>
        <TableCell>{formatCents(purchase.retailCents)}</TableCell>
        {onChangeDisposition && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            {purchase.workspaceId === PLATFORM_COLD_SENTINEL ? (
              <Select
                value={purchase.poolDisposition ?? "unassigned"}
                onValueChange={(v) => onChangeDisposition(purchase.id, v)}
              >
                <SelectTrigger
                  className="h-7 w-[130px] text-xs"
                  data-testid={`select-disposition-${purchase.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Sandbox</SelectItem>
                  <SelectItem value="leasable">Leasable</SelectItem>
                  <SelectItem value="platform_use">Reserved</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge
                variant="outline"
                className="text-xs"
                data-testid={`badge-disposition-${purchase.id}`}
              >
                Tenant-owned
              </Badge>
            )}
          </TableCell>
        )}
        <TableCell className="text-muted-foreground text-xs">
          {new Date(purchase.createdAt).toLocaleDateString()}
        </TableCell>
        <TableCell>
          {purchase.provisioningStatus !== "cancelled" && purchase.provisioningStatus !== "live" && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive h-7 px-2"
              onClick={(e) => { e.stopPropagation(); onCancel(purchase.id); }}
              data-testid={`button-cancel-purchase-${purchase.id}`}
            >
              Cancel
            </Button>
          )}
        </TableCell>
      </TableRow>

      {expanded && purchase.items && purchase.items.length > 0 && (
        <TableRow>
          <TableCell colSpan={onChangeDisposition ? 7 : 6} className="bg-muted/30 p-0">
            <div className="p-3 space-y-1">
              {/* Task #606 — send-from display name for leased carousels */}
              {purchase.provisioningStatus === "live" && onEditFromName && (
                <div
                  className="flex items-center gap-2 pb-2 mb-2 border-b text-sm"
                  data-testid={`from-name-row-${purchase.id}`}
                >
                  <span className="text-muted-foreground text-xs w-32 shrink-0">Send-from name</span>
                  <span
                    className="flex-1 font-medium truncate"
                    data-testid={`text-from-name-${purchase.id}`}
                  >
                    {purchase.provisionSpec?.fromName || (
                      <span className="text-muted-foreground italic">Not set</span>
                    )}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditFromName(purchase.id, purchase.provisionSpec?.fromName ?? "");
                    }}
                    data-testid={`button-edit-from-name-${purchase.id}`}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              )}
              {purchase.items.map((item) => (
                <div key={item.id} className="py-1.5" data-testid={`item-row-${item.id}`}>
                  <div className="flex items-center gap-3 text-sm">
                    {statusIcon(item.status)}
                    <span className="capitalize text-muted-foreground text-xs w-16">{item.itemType}</span>
                    <span className="font-medium flex-1">{item.identifier}</span>
                    {item.smtpHost && (
                      <span className="text-xs text-muted-foreground">{item.smtpHost}:{item.smtpPort}</span>
                    )}
                    {statusBadge(item.status)}
                    {(item.status === "failed" || item.status === "replacement_requested") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => { e.stopPropagation(); onReplaceItem(item.id); }}
                        data-testid={`button-replace-item-${item.id}`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Replace
                      </Button>
                    )}
                  </div>
                  {item.itemType === "mailbox" && item.status === "live" && (
                    <div className="ml-7">
                      <WarmupProgress createdAt={item.createdAt} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Dollar input (display in $, store in cents) ──────────────────────────────

function DollarInput({
  id,
  testId,
  cents,
  onChange,
}: {
  id: string;
  testId: string;
  cents: number;
  onChange: (cents: number) => void;
}) {
  const [display, setDisplay] = useState(() => (cents / 100).toFixed(2));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDisplay((cents / 100).toFixed(2));
    }
  }, [cents, focused]);

  return (
    <Input
      id={id}
      data-testid={testId}
      type="number"
      step="0.01"
      min="0"
      value={display}
      onChange={(e) => setDisplay(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const dollars = parseFloat(display || "0");
        const newCents = Math.round((isNaN(dollars) ? 0 : dollars) * 100);
        onChange(newCents);
        setDisplay((newCents / 100).toFixed(2));
      }}
    />
  );
}

// ─── Package editor dialog (platform) ─────────────────────────────────────────

function PackageEditorDialog({
  pkg,
  onClose,
}: {
  pkg: InfraPackage | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<InfraPackage>>(
    pkg ?? {
      name: "",
      description: "",
      purchaseType: "lease",
      domainsCount: 1,
      mailboxesCount: 4,
      billingMode: "flat_bundle",
      flatRateMonthlyWholesaleCents: 0,
      flatRateMonthlyRetailCents: 0,
      wholesaleCentsPerDomain: 900,
      wholesaleCentsPerMailbox: 150,
      retailCentsPerDomain: 1500,
      retailCentsPerMailbox: 250,
      domainBillingCycle: "annual",
      mailboxBillingCycle: "monthly",
      isActive: true,
      sortOrder: 0,
    },
  );
  const isFlat = form.billingMode === "flat_bundle";

  const saveMutation = useMutation({
    mutationFn: async () => {
      const method = pkg ? "PUT" : "POST";
      const url = pkg ? `/api/platform/infra/packages/${pkg.id}` : "/api/platform/infra/packages";
      const res = await apiRequest(method, url, form);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: pkg ? "Package updated" : "Package created" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/infra/packages"] });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message, variant: "destructive" });
    },
  });

  const field = (key: keyof InfraPackage, label: string, type = "text") => (
    <div className="space-y-1">
      <Label htmlFor={`pkg-${key}`}>{label}</Label>
      <Input
        id={`pkg-${key}`}
        data-testid={`input-pkg-${key}`}
        type={type}
        value={String(form[key] ?? "")}
        onChange={(e) =>
          setForm((f) => ({
            ...f,
            [key]: type === "number" ? Number(e.target.value) : e.target.value,
          }))
        }
      />
    </div>
  );

  const dollarField = (key: keyof InfraPackage, label: string) => (
    <div className="space-y-1">
      <Label htmlFor={`pkg-${key}`}>{label}</Label>
      <DollarInput
        id={`pkg-${key}`}
        testId={`input-pkg-${key}`}
        cents={form[key] as number}
        onChange={(cents) => setForm((f) => ({ ...f, [key]: cents }))}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{pkg ? "Edit Package" : "New Package"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">{field("name", "Name")}</div>
          <div className="col-span-2">
            <Label htmlFor="pkg-description">Description</Label>
            <VoiceToTextTextarea
              id="pkg-description"
              data-testid="input-pkg-description"
              value={form.description ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Type</Label>
            <div className="flex gap-3">
              {(["lease", "buy"] as const).map((type) => (
                <label
                  key={type}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer capitalize",
                    form.purchaseType === type
                      ? "border-primary bg-primary/5 font-medium"
                      : "border-muted-foreground/25 text-muted-foreground",
                  )}
                  data-testid={`label-pkg-type-${type}`}
                >
                  <input
                    type="radio"
                    name="purchaseType"
                    value={type}
                    checked={form.purchaseType === type}
                    onChange={() => setForm((f) => ({ ...f, purchaseType: type }))}
                    className="accent-primary"
                    data-testid={`radio-pkg-type-${type}`}
                  />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </label>
              ))}
            </div>
          </div>
          {field("domainsCount", "Domains", "number")}
          {field("mailboxesCount", "Mailboxes", "number")}
          <div className="col-span-2 space-y-1">
            <Label>Billing mode</Label>
            <div className="flex gap-3">
              {(["flat_bundle", "per_item"] as const).map((mode) => (
                <label
                  key={mode}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer",
                    form.billingMode === mode
                      ? "border-primary bg-primary/5 font-medium"
                      : "border-muted-foreground/25 text-muted-foreground",
                  )}
                  data-testid={`label-pkg-billing-${mode}`}
                >
                  <input
                    type="radio"
                    name="billingMode"
                    value={mode}
                    checked={form.billingMode === mode}
                    onChange={() => setForm((f) => ({ ...f, billingMode: mode }))}
                    className="accent-primary"
                    data-testid={`radio-pkg-billing-${mode}`}
                  />
                  {mode === "flat_bundle" ? "Flat monthly bundle" : "Per-item (domain + mailbox)"}
                </label>
              ))}
            </div>
          </div>
          {isFlat ? (
            <>
              {dollarField("flatRateMonthlyWholesaleCents", "Wholesale $/mn")}
              {dollarField("flatRateMonthlyRetailCents", "Retail $/mn")}
            </>
          ) : (
            <>
              {dollarField("wholesaleCentsPerDomain", "Wholesale $/domain")}
              {dollarField("wholesaleCentsPerMailbox", "Wholesale $/mailbox")}
              {dollarField("retailCentsPerDomain", "Retail $/domain")}
              {dollarField("retailCentsPerMailbox", "Retail $/mailbox")}
            </>
          )}
          {field("sortOrder", "Sort order", "number")}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saveMutation.isPending}>Cancel</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-package">
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Carousel pool status panel (platform only) ───────────────────────────────

type PoolCarouselSummary = {
  purchaseId: string;
  domainNames: string[];
  mailboxCount: number;
  isAvailable: boolean;
  isWarming: boolean;
  daysWarming: number | null;
  healthScore: number | null;
  lifecycleState: string | null;
};

function CarouselPoolPanel() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<{
    carousels: PoolCarouselSummary[];
    available: PoolCarouselSummary[];
    warming: PoolCarouselSummary[];
    other: PoolCarouselSummary[];
    total: number;
  }>({
    queryKey: ["/api/platform/infra/pool"],
    refetchInterval: 60_000,
  });

  const available = data?.available ?? [];
  const warming = data?.warming ?? [];
  const other = data?.other ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Carousel Pool</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Platform pre-warmed carousels — provisioned here, leased by tenants.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-refresh-pool"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading pool…
          </div>
        ) : isError ? (
          <div className="flex items-center gap-2 text-sm text-destructive py-2">
            <AlertTriangle className="h-4 w-4" />
            Failed to load pool.
          </div>
        ) : total === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No carousels in pool yet. Use the Packages tab to provision some.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Ready", count: available.length, color: "text-green-600" },
                { label: "Warming", count: warming.length, color: "text-amber-600" },
                { label: "Other", count: other.length, color: "text-muted-foreground" },
              ].map((s) => (
                <div key={s.label} className="rounded-md border p-3 text-center">
                  <div className={cn("text-2xl font-bold", s.color)}>{s.count}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Carousel rows */}
            <div className="rounded-md border divide-y" data-testid="list-carousel-pool">
              {data?.carousels.map((c) => (
                <div key={c.purchaseId} className="flex items-start justify-between gap-3 p-2.5">
                  <div className="flex items-start gap-2 min-w-0">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {c.domainNames.slice(0, 2).join(", ")}
                        {c.domainNames.length > 2 ? ` +${c.domainNames.length - 2}` : ""}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.mailboxCount} mailbox{c.mailboxCount !== 1 ? "es" : ""}
                        {c.daysWarming !== null ? ` · ${c.daysWarming}d warming` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs shrink-0 text-right">
                    {c.isAvailable ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                        <CheckCircle2 className="h-3 w-3" />
                        Ready
                      </span>
                    ) : c.isWarming ? (
                      <span className="inline-flex items-center gap-1 text-amber-600">
                        <Clock className="h-3 w-3" />
                        {c.healthScore !== null ? `${c.healthScore}%` : "Warming"}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{c.lifecycleState ?? "—"}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Post-checkout warm-up dialog (Task #600) ────────────────────────────────
// Tenants return here from a successful Stripe checkout. Instead of the old
// generic "purchase complete" message this binds to the purchase they just
// paid for and replays the wizard's warm-up (step 6) and confirmation (step 7)
// screens with REAL state: per-mailbox Instantly enrollment polled live, and
// the send-ramp explainer rendered with their actual addresses.

function PostCheckoutWarmupDialog({
  purchaseId,
  onClose,
}: {
  purchaseId: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<1 | 2>(1);
  const [startedAt] = useState(() => Date.now());
  // Re-render every 15s so the stall warning can appear without a data change.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, []);

  // Confirm payment with the server. The Stripe webhook is the authoritative
  // path, but this belt-and-braces call kicks provisioning immediately if the
  // webhook hasn't landed yet. Idempotent server-side.
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/infra/purchases/confirm", { purchaseId });
      return res.json();
    },
  });
  useEffect(() => {
    confirmMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId]);

  const itemsQuery = useQuery<any>({
    queryKey: [`/api/admin/infra/purchases/${purchaseId}/items`],
    // Poll every 5s until we reach a state where nothing more can change:
    // terminal purchase failure, or every mailbox has an enrollment outcome.
    refetchInterval: (query) => {
      const d: any = query.state.data;
      if (!d) return 5000;
      const status = d?.purchase?.provisioningStatus;
      if (status === "failed" || status === "cancelled") return false;
      const mbs = (d?.items ?? []).filter((i: any) => i?.itemType === "mailbox");
      const settled =
        mbs.length > 0 && mbs.every((m: any) => m.instantlyEmail || m.warmupEnrollmentError);
      return settled ? false : 5000;
    },
  });

  const purchase = itemsQuery.data?.purchase;
  const items: any[] = itemsQuery.data?.items ?? [];
  const mailboxes = items.filter((i) => i?.itemType === "mailbox");
  const domains = items.filter((i) => i?.itemType === "domain");
  const purchaseFailed =
    purchase?.provisioningStatus === "failed" || purchase?.provisioningStatus === "cancelled";
  const awaitingCapacity = purchase?.provisioningStatus === "awaiting_capacity";
  // Fail loudly rather than spinning forever: if 5+ minutes have passed and no
  // mailboxes have materialised, surface a stall warning (polling continues —
  // the server's own staleness guard will hard-fail a genuinely stuck order).
  const stalled =
    !purchaseFailed && mailboxes.length === 0 && Date.now() - startedAt > 5 * 60_000;
  const mailboxesCount = purchase?.mailboxesCount ?? Math.max(1, mailboxes.length);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md flex flex-col max-h-[90vh]" data-testid="dialog-post-checkout-warmup">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <DialogTitle className="text-lg">
              {phase === 1 ? "Payment received — setting up your mailboxes" : "What you can send, and when"}
            </DialogTitle>
          </div>
          <DialogDescription>
            {phase === 1
              ? "We're creating your domains and mailboxes and enrolling them in warm-up. This screen updates live."
              : "Your mailboxes warm up automatically. Here's the send schedule Clarik enforces."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1 overflow-y-auto flex-1 min-h-0">
          {phase === 1 && (
            <>
              {/* Loud failure states first */}
              {itemsQuery.isError && (
                <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-xs text-red-800 dark:text-red-300 space-y-2" data-testid="alert-post-checkout-error">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Couldn't load your order status
                  </p>
                  <p>{(itemsQuery.error as any)?.message ?? "Unknown error."} Your payment went through — this is only a status-display problem.</p>
                  <Button size="sm" variant="outline" onClick={() => itemsQuery.refetch()} data-testid="button-post-checkout-retry">
                    <RefreshCw className="mr-1.5 h-3 w-3" /> Retry
                  </Button>
                </div>
              )}

              {purchaseFailed && (
                <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-xs text-red-800 dark:text-red-300 space-y-1" data-testid="alert-post-checkout-failed">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Provisioning failed
                  </p>
                  <p>
                    {purchase?.notes || "The provider could not complete your order."} Your
                    subscription is cancelled automatically for failed orders — you won't keep
                    being billed. Contact support if a charge already went through.
                  </p>
                </div>
              )}

              {!purchaseFailed && awaitingCapacity && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-1" data-testid="alert-post-checkout-awaiting">
                  <p className="font-semibold flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 shrink-0" /> Paid — queued for delivery
                  </p>
                  <p className="text-muted-foreground">
                    Your payment went through, but we're briefly at capacity. Your mailboxes will
                    be provisioned automatically as soon as capacity is restored — no action needed
                    and you won't be charged again.
                  </p>
                </div>
              )}

              {!purchaseFailed && !awaitingCapacity && stalled && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-1" data-testid="alert-post-checkout-stalled">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Taking longer than expected
                  </p>
                  <p className="text-muted-foreground">
                    Provisioning usually finishes within a few minutes. We're still working on it —
                    you can safely close this window; a genuinely stuck order is failed and refunded
                    automatically within 2 hours, and you'll see the outcome in the My Orders tab.
                  </p>
                </div>
              )}

              {/* Live enrollment status */}
              {!purchaseFailed && (
                itemsQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking your order…
                  </div>
                ) : mailboxes.length === 0 ? (
                  !awaitingCapacity && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="text-post-checkout-provisioning">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Provisioning your mailboxes…
                      this usually takes a few minutes.
                    </div>
                  )
                ) : (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Warm-up enrollment</Label>
                    {mailboxes.map((mb: any) => {
                      const enrolled = !!mb.instantlyEmail;
                      const failed = !!mb.warmupEnrollmentError;
                      return (
                        <div
                          key={mb.id}
                          data-testid={`row-post-checkout-enrollment-${mb.id}`}
                          className="flex items-start justify-between gap-2 rounded border p-2 text-xs"
                        >
                          <span className="truncate font-mono">{mb.identifier}</span>
                          {failed ? (
                            <span className="text-destructive shrink-0 text-right">
                              Enrollment failed
                              <span className="block font-normal opacity-80">
                                {String(mb.warmupEnrollmentError).slice(0, 80)}
                              </span>
                            </span>
                          ) : enrolled ? (
                            <span className="text-green-600 dark:text-green-500 shrink-0">Enrolled in warm-up</span>
                          ) : (
                            <span className="text-muted-foreground shrink-0 flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" /> Enrolling…
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {mailboxes.some((m: any) => m.warmupEnrollmentError) && (
                      <p className="text-xs text-muted-foreground">
                        Failed enrollments are retried automatically every hour. No action needed
                        unless the error persists.
                      </p>
                    )}
                  </div>
                )
              )}

              {domains.length > 0 && !purchaseFailed && (
                <p className="text-xs text-muted-foreground">
                  Domains: <span className="font-mono">{domains.map((d) => d.identifier).join(", ")}</span>
                </p>
              )}
            </>
          )}

          {phase === 2 && (
            <>
              {/* The actual addresses this schedule applies to */}
              <div className="rounded-md border p-3 text-xs space-y-1.5" data-testid="post-checkout-addresses">
                <p className="font-medium">Your mailboxes</p>
                {mailboxes.length > 0 ? (
                  <div className="text-muted-foreground space-y-0.5">
                    {mailboxes.map((mb: any) => (
                      <p key={mb.id} className="font-mono truncate">{mb.identifier}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    Addresses will appear here once provisioning finishes — check the My Orders tab.
                  </p>
                )}
              </div>

              {/* Seasoning callout — the most critical fact */}
              <div className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 p-3 text-xs text-red-800 dark:text-red-300 space-y-1">
                <p className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Seasoning period: zero prospect sends
                </p>
                <p>
                  Every new mailbox spends <strong>~10+ days seasoning</strong> — building
                  reputation in the warm-up network and passing inbox-placement tests. You
                  cannot send prospect email during this window; Clarik enforces it
                  automatically. Placement test sends ({PLACEMENT_TEST_DAILY_BUDGET}/mailbox/day)
                  are allowed and don't count against the cap.
                </p>
              </div>

              <div className="rounded-md border overflow-hidden" data-testid="table-post-checkout-ramp">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Stage (after seasoning)</TableHead>
                      <TableHead className="text-xs text-right">Per mailbox / day</TableHead>
                      <TableHead className="text-xs text-right">Total / day ({mailboxesCount} mailboxes)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {COLD_SEND_TIER_GUIDE.map((entry, i) => {
                      const isWarmed = entry.tier === "warmed";
                      return (
                        <TableRow key={entry.tier} className={cn(isWarmed && "bg-green-500/5")} data-testid={`row-post-checkout-stage-${i}`}>
                          <TableCell className="text-xs font-medium py-2">
                            {entry.label}{" "}
                            <span className="text-muted-foreground font-normal">({entry.dayRange})</span>
                          </TableCell>
                          <TableCell className="text-xs text-right py-2">
                            {entry.cap.toLocaleString()} prospects/day
                          </TableCell>
                          <TableCell className={cn("text-xs text-right py-2 font-semibold", isWarmed && "text-green-600 dark:text-green-400")}>
                            {(entry.cap * mailboxesCount).toLocaleString()} prospects/day
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {phase === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="button-post-checkout-close">
                Close
              </Button>
              <Button onClick={() => setPhase(2)} data-testid="button-post-checkout-next">
                Next: send schedule
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPhase(1)} data-testid="button-post-checkout-back">
                Back
              </Button>
              <Button onClick={onClose} data-testid="button-post-checkout-done">
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────

export function InfraStorefront({
  surface,
  workspaceId,
}: {
  surface: "tenant" | "platform";
  workspaceId?: string;
}) {
  const { toast } = useToast();
  const [editPkg, setEditPkg] = useState<InfraPackage | null | undefined>(undefined);
  const [provisionWsId, setProvisionWsId] = useState(workspaceId ?? "");
  const [confirmProvisionPkg, setConfirmProvisionPkg] = useState<InfraPackage | null>(null);

  const { data: wsListData } = useQuery<any>({
    queryKey: ["/api/platform/workspaces", { limit: 300 }],
    queryFn: () =>
      fetch("/api/platform/workspaces?limit=300&offset=0", { credentials: "include" }).then((r) =>
        r.json(),
      ),
    enabled: surface === "platform",
  });
  const allWorkspaces: { id: string; name: string; slug: string }[] =
    wsListData?.workspaces ?? wsListData ?? [];
  // Task #600 — purchase id from the Stripe success redirect. Non-null opens
  // the post-checkout warm-up dialog bound to that specific purchase.
  const [postCheckoutPurchaseId, setPostCheckoutPurchaseId] = useState<string | null>(null);

  useEffect(() => {
    if (surface !== "tenant") return;
    const params = new URLSearchParams(window.location.search);
    const successId = params.get("infra_success") || params.get("infra_ready");
    if (successId) {
      setPostCheckoutPurchaseId(successId);
      params.delete("infra_success");
      params.delete("infra_ready");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [surface]);

  const purchasesKey = surface === "tenant"
    ? "/api/admin/infra/purchases"
    : "/api/platform/infra/purchases";

  const { data: packages = [], isLoading: pkgsLoading } = useQuery<InfraPackage[]>({
    queryKey: surface === "tenant" ? ["/api/admin/infra/packages"] : ["/api/platform/infra/packages"],
  });

  const { data: purchasesData, isLoading: purchasesLoading } = useQuery<{
    purchases: InfraPurchase[];
    pnl?: { totalRetailCents: number; totalWholesaleCents: number; totalMarginCents: number; purchaseCount: number };
  } | InfraPurchase[]>({
    queryKey: [purchasesKey],
  });

  const purchases: InfraPurchase[] = Array.isArray(purchasesData)
    ? purchasesData
    : (purchasesData as any)?.purchases ?? [];
  const pnl = !Array.isArray(purchasesData) ? (purchasesData as any)?.pnl : undefined;

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const base = surface === "tenant" ? "/api/admin/infra" : "/api/platform/infra";
      const res = await apiRequest("POST", `${base}/purchases/${id}/cancel`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Purchase cancelled" });
      queryClient.invalidateQueries({ queryKey: [purchasesKey] });
    },
    onError: (err: any) => {
      toast({ title: "Error cancelling", description: err?.message, variant: "destructive" });
    },
  });

  const replaceItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const base = surface === "tenant" ? "/api/admin/infra" : "/api/platform/infra";
      const res = await apiRequest("POST", `${base}/items/${itemId}/replace`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Replacement requested", description: "A new item will be provisioned by the provider." });
      queryClient.invalidateQueries({ queryKey: [purchasesKey] });
    },
    onError: (err: any) => {
      toast({ title: "Error requesting replacement", description: err?.message, variant: "destructive" });
    },
  });

  // Task #606 — tenant: edit the send-from display name on a live purchase.
  const [editFromNameTarget, setEditFromNameTarget] = useState<{ id: string; current: string } | null>(null);
  const [editFromNameValue, setEditFromNameValue] = useState("");
  const editFromNameMutation = useMutation({
    mutationFn: async ({ id, fromName }: { id: string; fromName: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/infra/purchases/${id}/from-name`, { fromName });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Send-from name updated", description: "New outbound emails will use the updated display name." });
      queryClient.invalidateQueries({ queryKey: [purchasesKey] });
      setEditFromNameTarget(null);
    },
    onError: (err: any) => {
      toast({ title: "Couldn't update name", description: err?.message, variant: "destructive" });
    },
  });

  // Task #599 — platform-only: publish / pull back a sentinel-owned carousel.
  const dispositionMutation = useMutation({
    mutationFn: async ({ id, poolDisposition }: { id: string; poolDisposition: string }) => {
      const res = await apiRequest("PATCH", `/api/platform/infra/purchases/${id}/disposition`, { poolDisposition });
      return res.json();
    },
    onSuccess: (_data, vars) => {
      const label = DISPOSITION_META[vars.poolDisposition]?.label ?? vars.poolDisposition;
      toast({
        title: "Disposition updated",
        description:
          vars.poolDisposition === "leasable"
            ? "This carousel is now offered in tenant lease pickers."
            : `This carousel is now ${label} — removed from tenant lease pickers.`,
      });
      queryClient.invalidateQueries({ queryKey: [purchasesKey] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't change disposition", description: err?.message, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: [purchasesKey] });
    },
  });

  const deletePkgMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/platform/infra/packages/${id}`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Package deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/infra/packages"] });
    },
  });

  // Task #397 — tenant resume/awaiting banner. Surfaces an in-flight order so a
  // re-entered wizard (after a timeout/lost connection) resumes the right step
  // instead of looking like a blank slate, and never re-triggers payment.
  const awaitingOrder = surface === "tenant"
    ? purchases.find((p) => p.provisioningStatus === "awaiting_capacity")
    : undefined;
  const provisioningOrder = surface === "tenant"
    ? purchases.find((p) => p.provisioningStatus === "provisioning")
    : undefined;
  const pendingOrder = surface === "tenant"
    ? purchases.find((p) => p.provisioningStatus === "pending_payment")
    : undefined;
  const liveOrder = surface === "tenant"
    ? purchases.find((p) => p.provisioningStatus === "live")
    : undefined;

  return (
    <div className="space-y-6">
      {/* Post-Stripe-checkout warm-up dialog (Task #600) — bound to the paid purchase */}
      {postCheckoutPurchaseId && (
        <PostCheckoutWarmupDialog
          purchaseId={postCheckoutPurchaseId}
          onClose={() => {
            setPostCheckoutPurchaseId(null);
            queryClient.invalidateQueries({ queryKey: [purchasesKey] });
          }}
        />
      )}

      {/* Task #397 — tenant in-flight order banner (resumable wizard state) */}
      {surface === "tenant" && awaitingOrder && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="banner-infra-awaiting">
          <CardContent className="py-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">We're setting up your cold email</p>
              <p className="text-muted-foreground mt-0.5">
                Your purchase went through. We're provisioning your domains and mailboxes now —
                you'll get an email when it's ready to finish setting up. You can safely leave this page;
                no need to pay again.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {surface === "tenant" && !awaitingOrder && provisioningOrder && (
        <Card className="border-blue-500/40 bg-blue-500/5" data-testid="banner-infra-provisioning">
          <CardContent className="py-4 flex items-start gap-3">
            <Loader2 className="h-5 w-5 text-blue-500 mt-0.5 shrink-0 animate-spin" />
            <div className="text-sm">
              <p className="font-medium">Provisioning your cold email…</p>
              <p className="text-muted-foreground mt-0.5">
                We're setting up your domains and mailboxes with the provider. This usually takes a few
                minutes — check the <span className="font-medium">My Orders</span> tab for live status.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {surface === "tenant" && !awaitingOrder && !provisioningOrder && pendingOrder && (
        <Card className="border-muted bg-muted/30" data-testid="banner-infra-pending">
          <CardContent className="py-4 flex items-start gap-3">
            <ShoppingCart className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">You have an order awaiting payment</p>
              <p className="text-muted-foreground mt-0.5">
                A previous checkout didn't finish. Re-select your package below to complete payment —
                we won't charge you twice for the same order.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {surface === "tenant" && !awaitingOrder && !provisioningOrder && !pendingOrder && liveOrder && (
        <Card className="border-green-500/40 bg-green-500/5" data-testid="banner-infra-ready">
          <CardContent className="py-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Your cold email is ready</p>
              <p className="text-muted-foreground mt-0.5">
                Your domains and mailboxes are live. See the <span className="font-medium">My Orders</span> tab
                to review them and finish setting up your outreach.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generator settings — platform only (Task #450) */}
      {surface === "platform" && <GeneratorSettingsCard />}

      {/* P&L summary — platform only */}
      {surface === "platform" && pnl && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Revenue", value: formatCents(pnl.totalRetailCents), icon: TrendingUp, color: "text-blue-500" },
            { label: "COGS", value: formatCents(pnl.totalWholesaleCents), icon: BarChart3, color: "text-muted-foreground" },
            { label: "Margin", value: formatCents(pnl.totalMarginCents), icon: TrendingUp, color: "text-green-500" },
            { label: "Orders", value: String(pnl.purchaseCount), icon: Package, color: "text-muted-foreground" },
          ].map((stat) => (
            <Card key={stat.label} className="p-4 flex items-center gap-3">
              <stat.icon className={cn("h-8 w-8 shrink-0", stat.color)} />
              <div>
                <div className="text-xs text-muted-foreground">{stat.label}</div>
                <div className="text-lg font-bold">{stat.value}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue={surface === "tenant" ? "lease" : "packages"}>
        <TabsList>
          {surface === "tenant" ? (
            <>
              <TabsTrigger value="lease" data-testid="tab-infra-lease">
                <Package className="mr-2 h-4 w-4" />Lease
              </TabsTrigger>
              <TabsTrigger value="buy" data-testid="tab-infra-buy">
                <ShoppingCart className="mr-2 h-4 w-4" />Buy
              </TabsTrigger>
            </>
          ) : (
            <TabsTrigger value="packages" data-testid="tab-infra-packages">
              <Package className="mr-2 h-4 w-4" />Packages
            </TabsTrigger>
          )}
          <TabsTrigger value="purchases" data-testid="tab-infra-purchases">
            <ShoppingCart className="mr-2 h-4 w-4" />
            {surface === "platform" ? "All Orders" : "My Orders"}
          </TabsTrigger>
        </TabsList>

        {/* ── Tenant Lease tab ── */}
        {surface === "tenant" && (
          <TabsContent value="lease" className="mt-4">
            {pkgsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : packages.filter((p) => p.purchaseType === "lease").length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="mx-auto h-10 w-10 mb-3 opacity-40" />
                <p>No lease packages available yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages
                  .filter((p) => p.purchaseType === "lease")
                  .map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      mode="lease"
                      surface={surface}
                    />
                  ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* ── Tenant Buy tab ── */}
        {surface === "tenant" && (
          <TabsContent value="buy" className="mt-4">
            {pkgsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : packages.filter((p) => p.purchaseType === "buy").length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="mx-auto h-10 w-10 mb-3 opacity-40" />
                <p>No buy packages available yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages
                  .filter((p) => p.purchaseType === "buy")
                  .map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      mode="buy"
                      surface={surface}
                    />
                  ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* ── Platform Packages tab (flat list) ── */}
        {surface === "platform" && (
          <TabsContent value="packages" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button size="sm" onClick={() => setEditPkg(null)} data-testid="button-new-package">
                <Plus className="mr-2 h-4 w-4" /> New Package
              </Button>
            </div>

            <div className="mb-4 flex items-center gap-3">
              <Label className="shrink-0 text-sm">Provision for workspace:</Label>
              <Select value={provisionWsId} onValueChange={setProvisionWsId}>
                <SelectTrigger
                  className="max-w-xs"
                  data-testid="select-provision-workspace"
                >
                  <SelectValue placeholder="Select workspace…" />
                </SelectTrigger>
                <SelectContent>
                  {allWorkspaces.map((ws) => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name}
                      <span className="ml-1.5 text-xs text-muted-foreground font-mono">
                        ({ws.slug})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {pkgsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="mx-auto h-10 w-10 mb-3 opacity-40" />
                <p>No packages configured yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    mode={pkg.purchaseType ?? "lease"}
                    surface={surface}
                    workspaceIdOverride={provisionWsId}
                    onEdit={(p) => setEditPkg(p)}
                    onDelete={(id) => deletePkgMutation.mutate(id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* ── Purchases tab ── */}
        <TabsContent value="purchases" className="mt-4">
          {purchasesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart className="mx-auto h-10 w-10 mb-3 opacity-40" />
              <p>No orders yet.</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Retail</TableHead>
                    {surface === "platform" && <TableHead>Disposition</TableHead>}
                    <TableHead>Date</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((p) => (
                    <PurchaseRow
                      key={p.id}
                      purchase={p}
                      onCancel={(id) => cancelMutation.mutate(id)}
                      onReplaceItem={(itemId) => replaceItemMutation.mutate(itemId)}
                      onChangeDisposition={
                        surface === "platform"
                          ? (id, poolDisposition) => dispositionMutation.mutate({ id, poolDisposition })
                          : undefined
                      }
                      onEditFromName={
                        surface === "tenant"
                          ? (id, current) => {
                              setEditFromNameValue(current);
                              setEditFromNameTarget({ id, current });
                            }
                          : undefined
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Package editor dialog */}
      {editPkg !== undefined && (
        <PackageEditorDialog pkg={editPkg} onClose={() => setEditPkg(undefined)} />
      )}

      {/* Task #606 — Edit send-from name dialog */}
      {editFromNameTarget && (
        <Dialog open onOpenChange={() => setEditFromNameTarget(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Edit send-from name</DialogTitle>
              <DialogDescription>
                This display name appears in the{" "}
                <span className="font-mono text-xs">From:</span> header on every
                email sent through this leased carousel. Mailbox addresses are
                not affected.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="edit-from-name-input">Display name</Label>
                <Input
                  id="edit-from-name-input"
                  value={editFromNameValue}
                  onChange={(e) => setEditFromNameValue(e.target.value)}
                  placeholder="e.g. The Booking Team"
                  maxLength={255}
                  data-testid="input-edit-from-name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editFromNameValue.trim()) {
                      editFromNameMutation.mutate({
                        id: editFromNameTarget.id,
                        fromName: editFromNameValue.trim(),
                      });
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setEditFromNameTarget(null)}
                data-testid="button-edit-from-name-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={!editFromNameValue.trim() || editFromNameMutation.isPending}
                onClick={() =>
                  editFromNameMutation.mutate({
                    id: editFromNameTarget.id,
                    fromName: editFromNameValue.trim(),
                  })
                }
                data-testid="button-edit-from-name-save"
              >
                {editFromNameMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
